import React, { useState, useEffect, useRef } from 'react';
import { useCartStore } from '../store/cartStore';
import BxgyAddYModal from './BxgyAddYModal';
import { useBxgyOffers, BxgyUnlockedCards } from './BxgyOffers';
import { useAuthStore } from '../store/authStore';
import { useActivityLogStore } from '../store/activityLogStore';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';

// Checkout payment-terminal (Ezetap) timer: Card 5 min, UPI 6 min. The status poll still runs every
// 10 s; the number of polls follows the timer so polling never stops before the countdown ends.
const EZETAP_TIMEOUT_SECONDS = { CARD: 300, UPI: 360 };

// ── Checkout draft persistence (cookies) ─────────────────────────────────────
// Customer, staff, and payment method are saved to a cookie so an INCOMPLETE checkout
// (modal closed, page refreshed, or cart corrected mid-payment) doesn't lose the cashier's
// entries — they're pre-filled next time. Cleared only when an order completes successfully.
const CHECKOUT_DRAFT_COOKIE = 'pos_checkout_draft';
const saveCheckoutDraft = (draft) => {
  try { document.cookie = `${CHECKOUT_DRAFT_COOKIE}=${encodeURIComponent(JSON.stringify(draft))};path=/;max-age=86400;samesite=lax`; }
  catch (e) { /* cookie write failed (size/permissions) — non-fatal */ }
};
const readCheckoutDraft = () => {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + CHECKOUT_DRAFT_COOKIE + '=([^;]*)'));
    return m ? JSON.parse(decodeURIComponent(m[1])) : null;
  } catch (e) { return null; }
};
const clearCheckoutDraft = () => {
  try { document.cookie = `${CHECKOUT_DRAFT_COOKIE}=;path=/;max-age=0;samesite=lax`; } catch (e) { /* non-fatal */ }
};

function CheckoutModal({ onClose, onComplete, locationId }) {
  const { cart, applyDiscount, removeDiscount } = useCartStore();
  // BXGY offers unlocked by this cart but not applied (shown as cards here too). Auto-apply and the
  // "no saving" warning run in the cart panel (the primary instance, mounted behind this modal).
  const { offers: bxgyOffers, autoErrors: bxgyAutoErrors, dismissed: bxgyDismissed } = useBxgyOffers();
  const { user } = useAuthStore();
  const [paymentMethod, setPaymentMethod] = useState(''); // always starts empty → coupon section enabled on open
  const [fulfillmentSource, setFulfillmentSource] = useState('store');
  const [allLocations, setAllLocations] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [discountToast, setDiscountToast] = useState(null);
  // BXGY "add Y to the cart" pop-up: { code, hint } while shown. Fires whenever an applied BXGY offer
  // ENTERS the 'need_y' state (applied with Y missing, or Y removed later) — once per transition.
  const [bxgyPrompt, setBxgyPrompt] = useState(null);
  const [addYFor, setAddYFor] = useState(null); // { code, needQty } while the "add offer item" picker is open
  const bxgyNeedYRef = useRef(new Set());
  const bxgyNeedYKey = (cart.discounts || []).filter(d => d.level === 'bxgy' && d.bxgyStatus === 'need_y').map(d => d.code).join('|');
  useEffect(() => {
    const now = new Set(bxgyNeedYKey ? bxgyNeedYKey.split('|') : []);
    const fresh = [...now].find(c => !bxgyNeedYRef.current.has(c));
    bxgyNeedYRef.current = now;
    if (fresh) {
      const d = (cart.discounts || []).find(x => x.code === fresh);
      setBxgyPrompt({ code: fresh, hint: (d && d.bxgyHint) || 'Add the offer item to the cart to get this offer.', needQty: (d && d.bxgyNeedQty) || 1 });
    } else if (now.size === 0) {
      setBxgyPrompt(null);
    }
  }, [bxgyNeedYKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const leftColRef = useRef(null);
  // When a coupon message appears (esp. an error), scroll the order-summary column to the top
  // so the message (pinned at the top) is immediately visible.
  useEffect(() => {
    if (discountToast && leftColRef.current) {
      leftColRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [discountToast]);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // populated from draft only after payment is selected
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [editingCustomerInfo, setEditingCustomerInfo] = useState(false);
  const [customerEditData, setCustomerEditData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerEditError, setCustomerEditError] = useState('');
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [splitCash, setSplitCash] = useState('');
  const [splitCard, setSplitCard] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [sendReceipt, setSendReceipt] = useState(true);
  const [gstBilling, setGstBilling] = useState(false);
  const [gstin, setGstin] = useState('');
  const [gstCompanyName, setGstCompanyName] = useState('');
  const [gstLookupLoading, setGstLookupLoading] = useState(false);
  const [diffBilling, setDiffBilling] = useState(false);
  const [billingAddressSource, setBillingAddressSource] = useState('new'); // 'existing' or 'new'
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState(null);
  const [billingAddress, setBillingAddress] = useState({ address1: '', address2: '', city: '', province: '', zip: '', country: 'India' });

  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  const isValidGST = (gst) => gstRegex.test(gst);
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [shippingAddress, setShippingAddress] = useState({
    address1: '',
    address2: '',
    city: '',
    province: '',
    zip: '',
    country: 'India'
  });
  const [pincodeVerified, setPincodeVerified] = useState(false);
  const [pincodeChecking, setPincodeChecking] = useState(false);
  const [pincodeResults, setPincodeResults] = useState(null);
  const [pincodeError, setPincodeError] = useState('');
  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  const [billingPincodeLookupLoading, setBillingPincodeLookupLoading] = useState(false);

  // Ezetap payment terminal state
  const hasEzetap = !!(user?.ezetapUsername && user?.ezetapDeviceId);
  const hasSnapmint = !!(user?.snapmintMerchantId?.trim() && user?.snapmintMerchantKey?.trim() && user?.snapmintToken?.trim());
  const [ezetapProcessing, setEzetapProcessing] = useState(false);
  const [ezetapStatus, setEzetapStatus] = useState(''); // INITIATED, POLLING, SUCCESS, FAILED
  const [ezetapError, setEzetapError] = useState('');
  const [ezetapDetails, setEzetapDetails] = useState(null);
  const [ezetapRefNumber, setEzetapRefNumber] = useState('');
  const [ezetapCountdown, setEzetapCountdown] = useState(0);
  // ROLLBACK(9fe12fe): per-store recovery switch (Worli, 1 Oct 2026) switched off again — uncomment the
  // block below, and the call sites marked ROLLBACK(9fe12fe), to restore.
  // const [recoveryEnabled, setRecoveryEnabled] = useState(false);
  // useEffect(() => {
  //   let alive = true;
  //   api.get('/checkout/ezetap/recovery-config', { params: { locationId } })
  //     .then(r => { if (alive) setRecoveryEnabled(!!(r.data && r.data.enabled)); })
  //     .catch(() => { if (alive) setRecoveryEnabled(false); });
  //   return () => { alive = false; };
  // }, [locationId]);
  const ezetapPollRef = useRef(null);
  const ezetapCountdownRef = useRef(null);
  const ezetapRequestIdRef = useRef(null);
  const ezetapSendingRef = useRef(false); // Guard against double-click sending duplicate requests to terminal
  // Manual txn-id recovery after a terminal timeout: context of the last attempt
  // (which flow / leg / amount) + the staff-entered Transaction ID from the slip
  const lastEzetapAttemptRef = useRef(null); // { kind: 'single'|'split'|'splitUpiCard', stepNum, mode, amount }
  const [recoverTxnInput, setRecoverTxnInput] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [recoverError, setRecoverError] = useState('');
  // "Already paid?" — place the order directly from a same-day charge-slip
  // without initiating a terminal payment first
  const [slipEntryOpen, setSlipEntryOpen] = useState(false);

  // Split Ezetap state: 2-step terminal flow (Cash then Card/UPI)
  const [splitStep, setSplitStep] = useState(0); // 0=not started, 1=cash processing, 2=card/upi processing, 3=both done
  const [splitCardMode, setSplitCardMode] = useState('CARD'); // CARD or UPI for 2nd leg

  // Split UPI+Card state: 2-step terminal flow (UPI then Card — both via terminal)
  const [splitUpiCardStep, setSplitUpiCardStep] = useState(0); // 0=not started, 1=UPI processing, 2=Card processing, 3=both done
  const [splitUpiAmount, setSplitUpiAmount] = useState('');
  const [splitCardAmount, setSplitCardAmount] = useState('');
  const [splitUpiCardDetails, setSplitUpiCardDetails] = useState([]); // [{txnId, p2pRequestId, ...}, ...]

  // Snapmint EMI payment state
  const [snapmintProcessing, setSnapmintProcessing] = useState(false);
  const [snapmintStatus, setSnapmintStatus] = useState(''); // INITIATED, POLLING, SUCCESS, FAILED
  const [snapmintError, setSnapmintError] = useState('');
  const [snapmintQr, setSnapmintQr] = useState('');
  const [snapmintUrl, setSnapmintUrl] = useState('');
  const [, setSnapmintOrderId] = useState(''); // snapmintOrderId used internally
  const [snapmintCountdown, setSnapmintCountdown] = useState(0);
  const [snapmintDetails, setSnapmintDetails] = useState(null);
  const snapmintPollRef = useRef(null);
  const snapmintCountdownRef = useRef(null);
  const [splitEzetapDetails, setSplitEzetapDetails] = useState([]); // [{txnId, p2pRequestId, ...}, ...]
  const autoCheckoutTriggeredRef = useRef(false);
  const [visibleDiscounts, setVisibleDiscounts] = useState([]);
  const [discountsLoading, setDiscountsLoading] = useState(false); // spinner while coupons lazy-load on expand
  const [posStaffs, setPosStaffs] = useState(user?.staffs || []);
  const [selectedStaff, setSelectedStaff] = useState('');
  // Collapsible "Available Coupons" — coupons are lazy-loaded only when this is expanded (fewer requests).
  const [couponsExpanded, setCouponsExpanded] = useState(false);

  // Block UI changes while payment is processing
  const paymentInProgress = snapmintProcessing || ezetapProcessing;
  // Lock discounts once a payment method is selected (prevent cart total changes during payment flow)
  const discountsLocked = !!paymentMethod || paymentInProgress;

  // Draft persistence (cookie). Captured ONCE on open into a ref so restore is decoupled from the
  // cookie's live value (the save effect below can't clobber a pending restore).
  const draftRef = useRef(readCheckoutDraft());
  const draftConsumedRef = useRef(false);
  const orderCompletedRef = useRef(false); // once an order is placed, never re-write the draft cookie

  // Bug 1: on open the coupon section must stay enabled (paymentMethod starts empty). Customer/staff
  // are pre-filled from the saved draft ONLY AFTER a payment method is selected — not on open.
  useEffect(() => {
    if (!paymentMethod || draftConsumedRef.current) return;
    draftConsumedRef.current = true;
    const d = draftRef.current;
    if (!d) return;
    if (d.staff && !selectedStaff) setSelectedStaff(d.staff);
    if (d.customer && !selectedCustomer) {
      // Restore the customer THROUGH selectCustomer so its addresses are actually fetched
      // (customerAddresses populated + default auto-selected). Then reconcile the exact address
      // the cashier had — matched against the freshly-fetched list — or a typed new address.
      // Restoring the customer directly (without fetching addresses) is what broke the address UI.
      selectCustomer(d.customer).then((addrs) => {
        const list = Array.isArray(addrs) ? addrs : [];
        if (d.addressId) {
          const match = list.find(a => String(a.id) === String(d.addressId));
          if (match) { handleSelectAddress(match); return; }
        }
        if (d.showNewAddress && d.shippingAddress && d.shippingAddress.address1) {
          setShowNewAddressForm(true);
          setShippingAddress(d.shippingAddress);
          setSelectedAddressId(null);
        }
      });
    }
  }, [paymentMethod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist customer / staff / payment method so an INCOMPLETE checkout survives a modal close,
  // refresh, or mid-payment cart correction. Never write the empty initial state (it would wipe a
  // pending restore). Cleared only on a successful order (see handleCheckout).
  useEffect(() => {
    if (orderCompletedRef.current) return; // order placed → draft cleared, don't recreate it
    if (!selectedCustomer && !selectedStaff && !paymentMethod) return;
    saveCheckoutDraft({
      customer: selectedCustomer, staff: selectedStaff, paymentMethod,
      addressId: selectedAddressId, shippingAddress, showNewAddress: showNewAddressForm,
    });
  }, [selectedCustomer, selectedStaff, paymentMethod, selectedAddressId, shippingAddress, showNewAddressForm]);

  // Truncate an over-long coupon code to a single line with an ellipsis so it never overlaps the
  // Apply/remove button or amount beside it. Apply to the code span; its flex parent needs
  // minWidth:0 and the siblings (badge/amount/button) need flexShrink:0 to stay visible.
  const codeTruncStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };

  // --- Level-based coupon helpers (Jul 2026) ---
  // Order-level coupons show in the subtotal/available area; product & collection level
  // coupons show attached to the specific cart line item they match.
  const _normId = (id) => { if (id == null) return null; const m = String(id).match(/(\d+)\s*$/); return m ? m[1] : String(id); };
  const isOrderLevel = (d) => (d?.level || 'order') === 'order';
  const isCustom = (d) => (d?.level || 'order') === 'custom';
  const isBxgy = (d) => (d?.level || 'order') === 'bxgy';
  const couponBadge = (d) => isBxgy(d) ? 'BXGY' : (d.level === 'custom' ? 'BUNDLE' : (d.level === 'collections' ? 'COLLECTION' : 'PRODUCT'));
  // BXGY: the server computes the exact per-line reward (d.bxgyAlloc, aligned to cart.items) — the
  // same split GST/invoice use — so the UI just reads it rather than re-deriving the offer.
  // A line rewarded as Y by this offer — including one that saves ₹0 (offer price ≥ item price),
  // so it still shows inside the offer block with the reason.
  const bxgyIsYLine = (d, item) => {
    const idx = (cart.items || []).indexOf(item);
    return idx >= 0 && !!(d.bxgyYLines || [])[idx];
  };
  const bxgyZeroNote = (d, item, share) => {
    if (!isBxgy(d) || share > 0.005 || !bxgyIsYLine(d, item)) return '';
    const rw = (d.bxgy && d.bxgy.reward) || {};
    return rw.type === 'fixed_price'
      ? `Offer price ${formatCurrency(rw.value)} is not lower than this item's price — no saving`
      : 'No saving from this offer';
  };
  const bxgyShare = (d, item) => {
    const idx = (cart.items || []).indexOf(item);
    return idx >= 0 ? Number((d.bxgyAlloc || [])[idx] || 0) : 0;
  };
  // "+ Add offer item" — opens the BXGY Y picker (BxgyAddYModal) so staff needn't search for Y.
  const addYButton = (code, needQty) => (
    <button onClick={() => setAddYFor({ code, needQty: needQty || 1 })} disabled={discountsLocked}
      style={{ padding: '3px 8px', background: discountsLocked ? '#d1d5db' : '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '10.5px', fontWeight: 700, cursor: discountsLocked ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
      ＋ Add offer item
    </button>
  );
  // Sub-line under a coupon block: offer text, or (BXGY) the "add Y" hint when there is one.
  const couponBlockNote = (d) => isBxgy(d)
    ? (d.bxgyHint || 'Buy X Get Y offer')
    : (d.valueType === 'percentage' ? `${d.value}% off these items` : `${formatCurrency(d.value)} off`);
  // Any ORDER-level discount applied? Order-level discounts reduce the total at the SUMMARY, not per
  // line, so the per-line struck MRP is hidden while one is active (it would be misleading). Lines
  // discounted by a product/collection/bundle coupon still show their struck MRP (the `discounted`
  // branch below is unaffected). (Sep 2026)
  const hasOrderLevelDiscount = (cart.discounts || []).some(d => isOrderLevel(d));
  // Target match only (ignores slab) — used to show which product a coupon is "for"
  const couponMatchesItem = (d, item) => {
    const level = d?.level || 'order';
    if (level === 'bxgy') return bxgyIsYLine(d, item) || bxgyShare(d, item) > 0;
    if (level === 'products') return (d.eligibleVariantIds || d.productVariantIds || []).map(String).includes(_normId(item.variantId));
    if (level === 'collections') return (d.eligibleProductIds || []).map(String).includes(_normId(item.productId));
    if (level === 'custom') {
      // A bundle item is any cart line matching ANY of the bundle's conditions (by variant or by the
      // condition's resolved collection product IDs). ALL matching units are discounted — the min
      // quantities only gate whether the coupon applies, they don't limit which units get it.
      return (d.conditions || []).some(c =>
        (c.variantIds || []).map(String).includes(_normId(item.variantId)) ||
        (c.productIds || []).map(String).includes(_normId(item.productId))
      );
    }
    return false;
  };
  // A line QUALIFIES for a product/collection coupon when it matches the target AND the COMBINED
  // total of ALL targeted items in the cart meets the slab (minimum) — the slab is on the eligible
  // items' combined total, not per line. Custom bundles just need to be a flagged bundle item
  // (their slab is checked against the whole bundle server-side).
  const couponQualifiesItem = (d, item) => {
    if (isCustom(d) || isBxgy(d)) return couponMatchesItem(d, item);
    if (!couponMatchesItem(d, item)) return false;
    const slab = Number(d.slab || 0);
    if (!slab) return true;
    const targetSum = (cart.items || [])
      .filter(it => couponMatchesItem(d, it))
      .reduce((s, it) => s + (it.lineTotal || 0), 0);
    return targetSum >= slab;
  };
  // Mirror of the server's applyMrpRepricing: a line is repriced to its MRP (compareAtPrice) when an
  // applied "apply on MRP" discount targets it AND MRP > price — so this preview matches the order the
  // server will create. effLineTotal is the MRP-based gross for such lines, the normal lineTotal otherwise.
  const isMrpRepriced = (item) =>
    !!item.compareAtPrice && parseFloat(item.compareAtPrice) > parseFloat(item.price) &&
    (cart.discounts || []).some(d => d && d.applyOnMrp && (isOrderLevel(d) || couponMatchesItem(d, item)));
  const effLineTotal = (item) =>
    isMrpRepriced(item)
      ? (parseFloat(item.compareAtPrice) + (item.personalizationFee || 0)) * item.quantity
      : (item.lineTotal || 0);

  // Subtotal of the qualifying lines — the base for the discount amount and eligibility.
  const scopedEligibleSubtotal = (discount) =>
    (cart.items || [])
      .filter(item => couponQualifiesItem(discount, item))
      .reduce((sum, item) => sum + effLineTotal(item), 0);

  // This line's share of a single scoped discount, and its total across ALL scoped discounts.
  const shareOfDiscount = (d, item) => {
    if (isBxgy(d)) return bxgyShare(d, item);
    const e = scopedEligibleSubtotal(d);
    return e > 0 ? (effLineTotal(item) / e) * (d.amount || 0) : 0;
  };
  const itemTotalScopedDiscount = (item) =>
    (cart.discounts || []).filter(d => !isOrderLevel(d) && couponQualifiesItem(d, item))
      .reduce((s, d) => s + shareOfDiscount(d, item), 0);

  // Renders a cart line as a card, with a green savings line for EACH scoped (product/collection/
  // bundle) coupon that discounts it. The item always stays inside its location group; order-level
  // coupons are NOT shown per-item (they're in the order-summary + Applied Coupons list).
  const renderItemLine = (item, key) => {
    const scopedForItem = (cart.discounts || []).filter(d => !isOrderLevel(d) && couponQualifiesItem(d, item));
    const total = itemTotalScopedDiscount(item);
    const net = Math.max(0, effLineTotal(item) - total);
    const discounted = total > 0.005;
    return (
      <div key={key} style={{ marginBottom: '6px', padding: '8px 10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', borderLeft: `3px solid ${discounted ? '#f59e0b' : '#3b82f6'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: '12px' }}>
          <div style={{ flex: 1, marginRight: '8px', minWidth: 0 }}>
            <div style={{ color: '#1e293b', fontWeight: '500', lineHeight: '1.3' }}>{item.title.split(' / ')[0]}</div>
            {item.title.includes(' / ') && <div style={{ fontSize: '10px', color: '#64748b', marginTop: '1px' }}>{item.title.split(' / ').slice(1).join(' / ')}</div>}
            <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>Qty: {item.quantity}</div>
            {item.customAttributes && item.customAttributes.length > 0 && (
              <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: '3px' }}>
                {item.customAttributes.map((attr, i) => (<span key={i} style={{ fontSize: '9px', color: '#c88346', background: '#fef3e2', padding: '1px 4px', borderRadius: '3px' }}>{attr.key}: {attr.value}</span>))}
              </div>
            )}
            {item.personalizationFee > 0 && <div style={{ fontSize: '10px', color: '#27ae60', marginTop: '1px' }}>+{formatCurrency(item.personalizationFee)} personalization</div>}
            {scopedForItem.map((d, i) => (
              <div key={i} style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                <span style={codeTruncStyle}>🎟️ {d.code}</span>
                <span style={{ fontSize: '7px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', padding: '1px 4px', borderRadius: '3px', flexShrink: 0 }}>{couponBadge(d)}</span>
                <span style={{ flexShrink: 0 }}>− {formatCurrency(shareOfDiscount(d, item))}</span>
              </div>
            ))}
          </div>
          <span style={{ fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', fontSize: '12px', textAlign: 'right' }}>
            <div style={{ color: discounted ? '#16a34a' : '#1e293b' }}>{formatCurrency(net)}</div>
            {discounted ? (
              <div style={{ fontSize: '9px', fontWeight: '400', color: '#a69889', textDecoration: 'line-through' }}>{formatCurrency(effLineTotal(item))}</div>
            ) : (!hasOrderLevelDiscount && item.compareAtPrice && parseFloat(item.compareAtPrice) > parseFloat(item.price) && (
              <div style={{ fontSize: '9px', fontWeight: '400', color: '#a69889', textDecoration: 'line-through' }}>{formatCurrency(parseFloat(item.compareAtPrice) * item.quantity)}</div>
            ))}
          </span>
        </div>
      </div>
    );
  };

  // Renders a cart line for the SINGLE-LOCATION coupon-block layout (item nested inside a coupon
  // card). thisShare = amount THIS coupon takes off the line; totalShare = amount ALL coupons take
  // off it (drives the net price). No per-coupon list here — the enclosing block names the coupon.
  const renderBlockItem = (item, key, thisShare = 0, totalShare = null, note = '') => {
    const total = totalShare == null ? thisShare : totalShare;
    const net = Math.max(0, effLineTotal(item) - total);
    const discounted = total > 0.005;
    return (
      <div key={key} style={{ marginBottom: '6px', padding: '8px 10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', borderLeft: `3px solid ${discounted ? '#f59e0b' : '#3b82f6'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: '12px' }}>
          <div style={{ flex: 1, marginRight: '8px', minWidth: 0 }}>
            <div style={{ color: '#1e293b', fontWeight: '500', lineHeight: '1.3' }}>{item.title.split(' / ')[0]}</div>
            {item.title.includes(' / ') && <div style={{ fontSize: '10px', color: '#64748b', marginTop: '1px' }}>{item.title.split(' / ').slice(1).join(' / ')}</div>}
            <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>Qty: {item.quantity}</div>
            {item.customAttributes && item.customAttributes.length > 0 && (
              <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: '3px' }}>
                {item.customAttributes.map((attr, i) => (<span key={i} style={{ fontSize: '9px', color: '#c88346', background: '#fef3e2', padding: '1px 4px', borderRadius: '3px' }}>{attr.key}: {attr.value}</span>))}
              </div>
            )}
            {item.personalizationFee > 0 && <div style={{ fontSize: '10px', color: '#27ae60', marginTop: '1px' }}>+{formatCurrency(item.personalizationFee)} personalization</div>}
            {thisShare > 0.005 && <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600, marginTop: '2px' }}>− {formatCurrency(thisShare)} from this coupon</div>}
            {note && <div style={{ fontSize: '10px', color: '#b45309', fontWeight: 600, marginTop: '2px' }}>⚠️ {note}</div>}
          </div>
          <span style={{ fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', fontSize: '12px', textAlign: 'right' }}>
            <div style={{ color: discounted ? '#16a34a' : '#1e293b' }}>{formatCurrency(net)}</div>
            {discounted ? (
              <div style={{ fontSize: '9px', fontWeight: '400', color: '#a69889', textDecoration: 'line-through' }}>{formatCurrency(effLineTotal(item))}</div>
            ) : (!hasOrderLevelDiscount && item.compareAtPrice && parseFloat(item.compareAtPrice) > parseFloat(item.price) && (
              <div style={{ fontSize: '9px', fontWeight: '400', color: '#a69889', textDecoration: 'line-through' }}>{formatCurrency(parseFloat(item.compareAtPrice) * item.quantity)}</div>
            ))}
          </span>
        </div>
      </div>
    );
  };

  // Signature of what's in the cart (variant + product) — drives re-fetch of eligible coupons.
  // Product/collection-level coupons appear/disappear as matching items are added/removed.
  const cartItemsKey = (cart.items || []).map(i => `${i.variantId}:${i.productId}`).join('|');

  // Fetch visible discount codes — ONLY when the "Available Coupons" section is expanded
  // (lazy load, so opening checkout doesn't fire this request until the cashier wants to see
  // coupons). Refetches when location/cart change WHILE expanded. Retries on a slow/flaky call.
  useEffect(() => {
    if (!couponsExpanded) return; // collapsed → don't load
    let cancelled = false;
    setDiscountsLoading(true);
    const fetchVisible = async (attempt = 1) => {
      try {
        const params = locationId ? `?locationId=${locationId}` : '';
        const res = await api.get(`/discounts/visible${params}`);
        if (!cancelled) { setVisibleDiscounts(res.data.discounts || []); setDiscountsLoading(false); }
      } catch (e) {
        if (!cancelled && attempt < 4) setTimeout(() => fetchVisible(attempt + 1), 500 * attempt);
        else if (!cancelled) { console.warn('Available coupons failed to load:', e?.message); setDiscountsLoading(false); }
      }
    };
    fetchVisible();
    return () => { cancelled = true; };
  }, [locationId, cartItemsKey, couponsExpanded]);

  // Fetch fresh staffs list for this location (in case metaobject was updated)
  useEffect(() => {
    api.get('/auth/my-staffs').then(res => {
      if (res.data?.staffs) setPosStaffs(res.data.staffs);
    }).catch(() => {});
  }, [locationId]);

  // Fetch locations to know store vs warehouse types
  useEffect(() => {
    api.get('/locations').then(res => {
      const locs = res.data.locations || [];
      setAllLocations(locs);
      // Auto-set fulfillment based on current location's type
      // BUT do NOT override to 'store' when cart has warehouse items
      const current = locs.find(l => l.id?.toString() === locationId?.toString());
      if (current?.type === 'warehouse') {
        setFulfillmentSource('warehouse');
      } else {
        // Only default to store if no warehouse/customized items in cart
        const cartHasWarehouse = cart.items.some(item => (item.locationType || 'store') === 'warehouse');
        const cartHasCustomized = cart.items.some(item => (item.customAttributes && item.customAttributes.length > 0) || (item.personalizationFee && item.personalizationFee > 0));
        if (!cartHasWarehouse && !cartHasCustomized) {
          setFulfillmentSource('store');
        }
      }
    }).catch(() => {});
  }, [locationId, cart.items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine effective locationId based on fulfillment source
  const warehouseLocation = allLocations.find(l => (l.type || 'store') === 'warehouse');
  const loginLocation = allLocations.find(l => l.id?.toString() === locationId?.toString());
  const effectiveLocationId = fulfillmentSource === 'warehouse'
    ? (warehouseLocation?.id?.toString() || locationId)
    : locationId;
  const effectiveLocationName = fulfillmentSource === 'warehouse'
    ? (warehouseLocation?.name || '')
    : (loginLocation?.name || '');

  // Check if any item has customization
  const hasCustomizedItem = cart.items.some(
    item => (item.customAttributes && item.customAttributes.length > 0) || (item.personalizationFee && item.personalizationFee > 0)
  );

  // Check if cart has items from different locations
  const locationIds = [...new Set(cart.items.map(item => item.locationId || locationId))];
  const isMultiLocation = locationIds.length > 1;

  // Check if any warehouse items exist (need shipping address)
  const hasWarehouseItems = cart.items.some(item => (item.locationType || 'store') === 'warehouse');

  // Force warehouse fulfillment when cart has customized items or warehouse items
  useEffect(() => {
    if (hasCustomizedItem || hasWarehouseItems) {
      setFulfillmentSource('warehouse');
    }
  }, [hasCustomizedItem, hasWarehouseItems]);

  // Reset payment to cash if switching fulfillment source
  useEffect(() => {
    // no-op: all payment methods valid for both sources
  }, [fulfillmentSource, paymentMethod]);

  // Reset split amounts and Ezetap split state when switching away from split
  useEffect(() => {
    if (paymentMethod !== 'split') {
      setSplitCash('');
      setSplitCard('');
      setSplitStep(0);
      setSplitCardMode('CARD');
      setSplitEzetapDetails([]);
    }
    if (paymentMethod !== 'splitUpiCard') {
      setSplitUpiAmount('');
      setSplitCardAmount('');
      setSplitUpiCardStep(0);
      setSplitUpiCardDetails([]);
    }
    setRecoverTxnInput('');
    setRecoverError('');
    setSlipEntryOpen(false);
  }, [paymentMethod]);

  // Reset pincode verification when address changes
  useEffect(() => {
    setPincodeVerified(false);
    setPincodeResults(null);
    setPincodeError('');
  }, [shippingAddress.zip]);

  // Auto-place order after successful terminal payment (Card/UPI, Split, Snapmint)
  useEffect(() => {
    if (processing || autoCheckoutTriggeredRef.current) return;

    const isCardUpiSuccess = (paymentMethod === 'card' || paymentMethod === 'upi') && ezetapStatus === 'SUCCESS' && !ezetapProcessing && transactionId;
    const isSnapmintSuccess = paymentMethod === 'snapmint' && snapmintStatus === 'SUCCESS' && !snapmintProcessing && transactionId;
    const isSplitSuccess = paymentMethod === 'split' && splitStep === 3 && !ezetapProcessing && transactionId;
    const isSplitUpiCardSuccess = paymentMethod === 'splitUpiCard' && splitUpiCardStep === 3 && !ezetapProcessing && transactionId;

    if (isCardUpiSuccess || isSnapmintSuccess || isSplitSuccess || isSplitUpiCardSuccess) {
      autoCheckoutTriggeredRef.current = true;
      // Small delay to ensure all state updates are flushed
      setTimeout(() => {
        handleCheckout();
      }, 500);
    }
  }, [ezetapStatus, snapmintStatus, splitStep, splitUpiCardStep, ezetapProcessing, snapmintProcessing, transactionId, paymentMethod, processing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset auto-checkout flag when payment method changes
  useEffect(() => {
    autoCheckoutTriggeredRef.current = false;
  }, [paymentMethod]);

  const verifyPincode = async () => {
    const pincode = shippingAddress.zip?.trim();
    if (!pincode || pincode.length < 5) {
      setPincodeError('Please enter a valid pincode');
      return;
    }

    setPincodeChecking(true);
    setPincodeError('');
    setPincodeResults(null);

    try {
      // Delivery timelines apply ONLY to warehouse-dispatched items — NOT in-store-pickup items.
      // So verify pincode only for warehouse-location items (a multi-location order keeps its
      // store lines out of the pincode/delivery check).
      const items = cart.items.filter(item => (item.locationType || 'store') === 'warehouse').map(item => {
        const attrs = item.customAttributes || [];
        const customSizeAttr = attrs.find(a => a.key === '_custom_size' || a.key === 'custom_size');
        const customSizeVal = (customSizeAttr?.value || '');
        const isMto = customSizeVal.includes('_ismto');
        const isCustomSize = !!customSizeAttr && !isMto;
        return {
          sku: item.sku,
          title: item.title,
          isCustom: isCustomSize,
          isMto,
          isCustomSize
        };
      });
      console.log('Pincode verify items sent:', JSON.stringify(items.map(i => ({ sku: i.sku, isMto: i.isMto, isCustomSize: i.isCustomSize, isCustom: i.isCustom }))));

      const response = await api.post('/checkout/verify-pincode', { pincode, items });
      console.log('Pincode verify response:', JSON.stringify(response.data.results.map(r => ({ title: r.title, isMto: r.isMto, isCustomSize: r.isCustomSize }))));
      setPincodeResults(response.data.results);
      if (response.data.success) {
        setPincodeVerified(true);
        setPincodeError('');
      } else {
        setPincodeVerified(false);
        setPincodeError('Some items cannot be delivered to this pincode');
      }
    } catch (error) {
      console.error('Pincode verification error:', error);
      setPincodeError(error.response?.data?.error || 'Failed to verify pincode');
      setPincodeVerified(false);
    } finally {
      setPincodeChecking(false);
    }
  };

  const searchTimerRef = useRef(null);

  const searchCustomers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await api.get('/customers/search', { params: { q: query } });
      setSearchResults(response.data.customers);
    } catch (error) {
      console.error('Error searching customers:', error);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setCustomerSearch(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (query.length >= 2) {
      searchTimerRef.current = setTimeout(() => searchCustomers(query), 500);
    } else {
      setSearchResults([]);
    }
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setSearchResults([]);
    setShowNewCustomerForm(false);
    useActivityLogStore.getState().logCustomerSelected(customer.firstName + ' ' + (customer.lastName || ''), customer.email);
    
    // Fetch customer addresses
    try {
      const response = await api.get(`/customers/${customer.id}`);
      console.log('🔍 Customer API response:', response.data);
      
      const addresses = response.data.customer.addresses || [];
      console.log('🔍 Addresses found:', addresses.length);
      console.log('🔍 Address data:', addresses);
      
      setCustomerAddresses(addresses);
      
      // Auto-select default address only for warehouse orders (address is mandatory there)
      if (fulfillmentSource === 'warehouse') {
        if (response.data.customer.defaultAddress) {
          console.log('✅ Using default address:', response.data.customer.defaultAddress.id);
          const defaultAddr = response.data.customer.defaultAddress;
          setSelectedAddressId(defaultAddr.id);
          setShippingAddress({
            address1: defaultAddr.address1 || '',
            address2: defaultAddr.address2 || '',
            city: defaultAddr.city || '',
            province: defaultAddr.province || '',
            zip: defaultAddr.zip || '',
            country: defaultAddr.country || 'India'
          });
          setShowNewAddressForm(false);
        } else if (addresses.length > 0) {
          console.log('✅ Using first address:', addresses[0].id);
          const firstAddr = addresses[0];
          setSelectedAddressId(firstAddr.id);
          setShippingAddress({
            address1: firstAddr.address1 || '',
            address2: firstAddr.address2 || '',
            city: firstAddr.city || '',
            province: firstAddr.province || '',
            zip: firstAddr.zip || '',
            country: firstAddr.country || 'India'
          });
          setShowNewAddressForm(false);
        } else {
          setShowNewAddressForm(true);
          setSelectedAddressId(null);
        }
      } else {
        // Store orders: no address pre-selected (optional)
        setSelectedAddressId(null);
        setShowNewAddressForm(false);
      }
      return addresses; // let callers (e.g. draft restore) reconcile a specific saved address
    } catch (error) {
      console.error('Error fetching customer addresses:', error);
      setShowNewAddressForm(true);
      return [];
    }
  };

  // Check if selected customer has missing info
  const getCustomerMissingFields = (cust) => {
    if (!cust) return [];
    const missing = [];
    if (!cust.firstName) missing.push('First Name');
    if (!cust.lastName) missing.push('Last Name');
    if (!cust.email) missing.push('Email');
    if (!cust.phone) missing.push('Phone');
    return missing;
  };

  const handleEditCustomerInfo = () => {
    setCustomerEditData({
      firstName: selectedCustomer.firstName || '',
      lastName: selectedCustomer.lastName || '',
      email: selectedCustomer.email || '',
      phone: selectedCustomer.phone || ''
    });
    setEditingCustomerInfo(true);
  };

  const handleSaveCustomerInfo = async () => {
    const { firstName, lastName, email, phone } = customerEditData;
    if (!firstName.trim() || !lastName.trim()) {
      alert('First name and last name are required');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      alert('At least email or phone is required');
      return;
    }
    setSavingCustomer(true);
    setCustomerEditError('');
    try {
      const response = await api.put(`/customers/${selectedCustomer.id}`, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined
      });
      if (response.data.success) {
        setSelectedCustomer({ ...selectedCustomer, ...response.data.customer });
        setEditingCustomerInfo(false);
        setCustomerEditError('');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      const raw = error.response?.data?.error || 'Failed to update customer';
      // Parse error into user-friendly message
      let msg = raw;
      try {
        const parsed = typeof raw === 'string' && raw.startsWith('{') ? JSON.parse(raw) : raw;
        if (typeof parsed === 'object') {
          const friendlyMessages = Object.entries(parsed).map(([field, errors]) => {
            const errText = Array.isArray(errors) ? errors.join(', ') : errors;
            if (field === 'phone' && errText.includes('already been taken')) {
              return 'A customer already exists with this phone number. Please search for the customer using this phone number instead.';
            } else if (field === 'email' && errText.includes('already been taken')) {
              return 'A customer already exists with this email address. Please search for the customer using this email instead.';
            } else if (errText.includes('invalid')) {
              return `The ${field} entered is invalid. Please check and try again.`;
            }
            return `${field}: ${errText}`;
          });
          msg = friendlyMessages.join(' ');
        }
      } catch (e) { /* use raw */ }
      setCustomerEditError(msg);
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.phone) {
      alert('Mobile number is required');
      return;
    }

    if (!newCustomer.email) {
      alert('Email is required');
      return;
    }

    if (!newCustomer.firstName || !newCustomer.lastName) {
      alert('First name and last name are required');
      return;
    }

    try {
      const response = await api.post('/customers', newCustomer);
      if (response.data.success) {
        const customer = response.data.customer;
        setSelectedCustomer(customer);
        setShowNewCustomerForm(false);
        setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
        setCustomerAddresses([]);
        setShowNewAddressForm(true); // New customer needs address
        alert('Customer created successfully');
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      const msg = error.response?.data?.error || 'Failed to create customer';
      alert(`Error: ${msg}`);
    }
  };

  const handleSelectAddress = (address) => {
    setSelectedAddressId(address.id);
    setShippingAddress({
      address1: address.address1 || '',
      address2: address.address2 || '',
      city: address.city || '',
      province: address.province || '',
      zip: address.zip || '',
      country: address.country || 'India'
    });
    setShowNewAddressForm(false);
  };

  // Clean up Ezetap polling and countdown on unmount
  useEffect(() => {
    return () => {
      if (ezetapPollRef.current) clearInterval(ezetapPollRef.current);
      if (ezetapCountdownRef.current) clearInterval(ezetapCountdownRef.current);
      if (snapmintPollRef.current) clearInterval(snapmintPollRef.current);
      if (snapmintCountdownRef.current) clearInterval(snapmintCountdownRef.current);
    };
  }, []);

  // Warn user before page refresh/close during active terminal payment
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (ezetapStatus === 'POLLING' || ezetapStatus === 'INITIATED' || ezetapStatus === 'RECONCILING') {
        e.preventDefault();
        e.returnValue = 'Payment is being processed on terminal. Leaving may cause payment without order.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [ezetapStatus]);

  // Persist pending payment to localStorage so it can be recovered after page refresh
  useEffect(() => {
    if (ezetapStatus === 'POLLING' && ezetapRequestIdRef.current && ezetapRefNumber) {
      localStorage.setItem('pendingEzetapPayment', JSON.stringify({
        p2pRequestId: ezetapRequestIdRef.current,
        refNumber: ezetapRefNumber,
        amount: cart.total,
        paymentMethod,
        initiatedAt: Date.now()
      }));
    } else if (ezetapStatus === 'SUCCESS' || ezetapStatus === 'FAILED' || ezetapStatus === '') {
      localStorage.removeItem('pendingEzetapPayment');
    }
  }, [ezetapStatus, ezetapRefNumber, cart.total, paymentMethod]);

  // On mount: check for pending payment from a previous page refresh
  useEffect(() => {
    const pending = localStorage.getItem('pendingEzetapPayment');
    if (!pending) return;
    try {
      const data = JSON.parse(pending);
      // Recover only while the payment window is still open (UPI 6 min, Card 5 min)
      const windowMs = (data.paymentMethod === 'upi' ? EZETAP_TIMEOUT_SECONDS.UPI : EZETAP_TIMEOUT_SECONDS.CARD) * 1000;
      if (Date.now() - data.initiatedAt > windowMs) {
        localStorage.removeItem('pendingEzetapPayment');
        return;
      }
      // Resume polling for this payment
      setEzetapRefNumber(data.refNumber);
      setEzetapStatus('POLLING');
      setEzetapProcessing(true);
      ezetapRequestIdRef.current = data.p2pRequestId;
      setEzetapCountdown(Math.max(0, Math.floor((windowMs - (Date.now() - data.initiatedAt)) / 1000)));

      // Start countdown
      ezetapCountdownRef.current = setInterval(() => {
        setEzetapCountdown(prev => {
          if (prev <= 1) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; return 0; }
          return prev - 1;
        });
      }, 1000);

      // Resume polling
      let pollCount = 0;
      const maxPolls = Math.ceil((windowMs - (Date.now() - data.initiatedAt)) / 10000);
      ezetapPollRef.current = setInterval(async () => {
        pollCount++;
        try {
          const statusRes = await api.post('/checkout/ezetap/status', {
            p2pRequestId: data.p2pRequestId,
            ezetapUsername: user?.ezetapUsername || ''
          });
          const pStatus = (statusRes.data.status || '').toUpperCase();
          if (pStatus === 'SUCCESS' || pStatus === 'AUTHORIZED') {
            clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
            if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
            setEzetapCountdown(0);
            setEzetapStatus('SUCCESS');
            setTransactionId(statusRes.data.transactionId || data.p2pRequestId);
            setEzetapDetails({
              txnId: statusRes.data.transactionId || '',
              p2pRequestId: data.p2pRequestId,
              receiptUrl: statusRes.data.receiptUrl || '',
              paymentMode: statusRes.data.paymentMode || data.paymentMethod,
              externalRefNumber: data.refNumber,
              raw: statusRes.data.raw || {}
            });
            setEzetapProcessing(false);
            localStorage.removeItem('pendingEzetapPayment');
          } else if (pStatus === 'FAILED' || pStatus === 'CANCELLED' || pStatus === 'DECLINED') {
            clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
            if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
            setEzetapCountdown(0);
            setEzetapStatus('FAILED');
            setEzetapError(`Payment ${pStatus.toLowerCase()} on terminal`);
            setEzetapProcessing(false);
            ezetapSendingRef.current = false;
            localStorage.removeItem('pendingEzetapPayment');
          }
        } catch (err) {
          console.error('Recovery poll error:', err);
        }
        if (pollCount >= maxPolls) {
          clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
          if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
          setEzetapCountdown(0);
          setEzetapStatus('FAILED');
          setEzetapError('Payment recovery timed out. Check terminal for status.');
          setEzetapProcessing(false);
          ezetapSendingRef.current = false;
          localStorage.removeItem('pendingEzetapPayment');
        }
      }, 10000);
    } catch (e) {
      localStorage.removeItem('pendingEzetapPayment');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared pre-payment validation — runs BEFORE any terminal/EMI payment is initiated
  // so staff cannot capture payment when GST or other mandatory fields are invalid.
  // Reset the payment phase back to "choose a method" — used when a coupon change is detected so
  // the cashier re-confirms the new total. Clearing paymentMethod also unlocks the coupon section
  // (discountsLocked = !!paymentMethod || paymentInProgress).
  const resetPaymentPhase = () => {
    setPaymentMethod('');
    setTransactionId('');
    setEzetapProcessing(false);
    setEzetapStatus('');
    setEzetapError('');
    setSnapmintProcessing(false);
    setSnapmintStatus('');
    setSplitStep(0);
    setSplitUpiCardStep(0);
    ezetapSendingRef.current = false;
  };

  // Re-validate the applied coupons against the LATEST manager config right before money moves.
  // Runs for EVERY payment method (called at the top of each payment initiator). If a manager
  // changed/removed a coupon after it was applied, the cart is corrected, the coupon section is
  // re-enabled, and the cashier is alerted — the payment is aborted so nothing is charged at a
  // stale total. Returns true when it's safe to proceed. Fails open (server re-checks at order-create).
  const revalidateBeforePayment = async () => {
    try {
      const res = await api.post('/cart/revalidate', { locationId, cart });
      if (res.data?.cart) useCartStore.setState({ cart: res.data.cart });
      const changes = res.data?.changes || [];
      if (changes.length > 0) {
        resetPaymentPhase(); // re-enable the coupon section
        setCouponsExpanded(true); // open the coupon list so the cashier sees/re-applies
        setDiscountToast({ type: 'error', message: `${changes.map(c => c.reason).join(' · ')}. Please review the updated total before taking payment.` });
        return false;
      }
      return true;
    } catch (e) {
      // Don't block the sale on a transient revalidation error — order-create re-checks server-side.
      console.warn('Coupon revalidation skipped:', e?.message);
      return true;
    }
  };

  const validatePrePayment = () => {
    if (cart.items.length === 0) {
      alert('Cart is empty');
      return false;
    }
    if (posStaffs.length > 0 && !selectedStaff) {
      alert('Please select a staff name');
      return false;
    }
    const currentLoc = allLocations.find(l => l.id?.toString() === locationId?.toString());
    const isStoreLocation = !currentLoc || currentLoc?.type?.toLowerCase() === 'store';
    if (isStoreLocation && !selectedCustomer) {
      alert('Customer selection is mandatory for store orders');
      return false;
    }
    if (fulfillmentSource === 'warehouse' && !selectedCustomer) {
      alert('Customer details are required for warehouse fulfillment/shipping orders');
      return false;
    }
    if (hasWarehouseItems || (fulfillmentSource === 'warehouse' && !isMultiLocation)) {
      if (!selectedAddressId && !showNewAddressForm) {
        alert('Please select a shipping address or add a new one');
        return false;
      }
      if (showNewAddressForm || !selectedAddressId) {
        if (!shippingAddress.address1 || !shippingAddress.city || !shippingAddress.province || !shippingAddress.zip) {
          alert('Complete shipping address is required for warehouse fulfillment');
          return false;
        }
      }
      if (!pincodeVerified) {
        alert('Please verify the delivery pincode before checkout');
        return false;
      }
    }
    // Address is COMPULSORY for store orders too — require a selected address or a complete new one
    // (pincode verification stays warehouse-only).
    if (fulfillmentSource === 'store' && isStoreLocation) {
      const addingNew = showNewAddressForm || customerAddresses.length === 0;
      if (!selectedAddressId && !addingNew) {
        alert('Please select a customer address or add a new one');
        return false;
      }
      if (addingNew || !selectedAddressId) {
        if (!shippingAddress.address1 || !shippingAddress.city || !shippingAddress.province || !shippingAddress.zip) {
          alert('Please enter a complete address (address, city, state, pincode)');
          return false;
        }
      }
    }
    if (gstBilling) {
      if (!selectedCustomer) {
        alert('Customer selection is mandatory for GST Billing');
        return false;
      }
      if (!gstin.trim()) {
        alert('Please enter GST Number for GST Billing');
        return false;
      }
      if (!isValidGST(gstin.trim().toUpperCase())) {
        alert('Invalid GSTIN format. Please enter a valid 15-character GSTIN (e.g. 22AAAAA0000A1Z5)');
        return false;
      }
      if (!gstCompanyName.trim()) {
        alert('Please enter Company Name for GST Billing');
        return false;
      }
      const effShippingAddr = selectedAddressId
        ? customerAddresses.find(a => a.id === selectedAddressId)
        : shippingAddress;
      if (!effShippingAddr || !effShippingAddr.address1?.trim() || !effShippingAddr.city?.trim() || !effShippingAddr.zip?.trim()) {
        alert('Shipping Address is required for GST Billing. Please select or add a shipping address with Address, City and Pincode.');
        return false;
      }
      const effBillingAddr = diffBilling
        ? (selectedBillingAddressId ? customerAddresses.find(a => a.id === selectedBillingAddressId) : billingAddress)
        : effShippingAddr;
      if (!effBillingAddr || !effBillingAddr.address1?.trim() || !effBillingAddr.city?.trim() || !effBillingAddr.zip?.trim()) {
        alert('Billing Address is required for GST Billing. Please provide Address, City and Pincode.');
        return false;
      }
    }
    return true;
  };

  // Initiate Snapmint EMI payment — show QR + link, poll for 10 minutes
  const handleSnapmintPayment = async () => {
    if (!validatePrePayment()) return;
    if (!(await revalidateBeforePayment())) return; // re-check coupons before charging
    if (!selectedCustomer) {
      alert('Please select a customer before initiating Snapmint payment');
      return;
    }
    if (!selectedCustomer.phone) {
      alert('Customer must have a phone number for Snapmint EMI');
      return;
    }

    setSnapmintProcessing(true);
    setSnapmintStatus('INITIATED');
    setSnapmintError('');
    setSnapmintQr('');
    setSnapmintUrl('');
    setSnapmintDetails(null);
    setTransactionId('');

    const orderId = `POS-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setSnapmintOrderId(orderId);

    try {
      const initRes = await api.post('/checkout/snapmint/initiate', {
        orderId,
        orderValue: parseFloat(cart.total),
        snapmintMerchantId: user?.snapmintMerchantId || '',
        snapmintMerchantKey: user?.snapmintMerchantKey || '',
        snapmintToken: user?.snapmintToken || '',
        customer: {
          firstName: selectedCustomer.firstName || '',
          lastName: selectedCustomer.lastName || '',
          fullName: `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim(),
          email: selectedCustomer.email || '',
          mobile: selectedCustomer.phone?.replace(/\D/g, '').slice(-10) || ''
        },
        products: cart.items.map(item => ({
          sku: item.sku || item.variantId || '',
          name: item.title || '',
          quantity: item.quantity || 1,
          unitPrice: item.price || 0
        })),
        shippingAddress,
        billingAddress
      });

      if (!initRes.data.success) {
        setSnapmintStatus('FAILED');
        setSnapmintError(initRes.data.error || 'Failed to initiate Snapmint checkout');
        setSnapmintProcessing(false);
        return;
      }

      setSnapmintQr(initRes.data.qrImage || '');
      setSnapmintUrl(initRes.data.redirectUrl || '');
      setSnapmintStatus('POLLING');

      // 10 minutes = 600 seconds, poll every 10 seconds = 60 polls
      const totalSeconds = 600;
      const maxPolls = 60;
      let pollCount = 0;

      setSnapmintCountdown(totalSeconds);
      if (snapmintCountdownRef.current) clearInterval(snapmintCountdownRef.current);
      snapmintCountdownRef.current = setInterval(() => {
        setSnapmintCountdown(prev => {
          if (prev <= 1) {
            clearInterval(snapmintCountdownRef.current);
            snapmintCountdownRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      snapmintPollRef.current = setInterval(async () => {
        pollCount++;
        try {
          const statusRes = await api.post('/checkout/snapmint/status', { orderId, snapmintToken: user?.snapmintToken || '' });
          const oStatus = statusRes.data.orderStatus;

          if (oStatus === 'CAPTURED' || oStatus === 'AUTHORIZED' || oStatus === 'SETTLED') {
            clearInterval(snapmintPollRef.current);
            snapmintPollRef.current = null;
            if (snapmintCountdownRef.current) { clearInterval(snapmintCountdownRef.current); snapmintCountdownRef.current = null; }
            setSnapmintCountdown(0);
            setSnapmintStatus('SUCCESS');
            setTransactionId(String(statusRes.data.snapmintId || statusRes.data.orderId || orderId));
            setSnapmintDetails({
              snapmintId: statusRes.data.snapmintId || '',
              snapmintOrderId: statusRes.data.snapmintOrderId || '',
              snapmintCartId: statusRes.data.snapmintCartId || '',
              dpRefNo: statusRes.data.dpRefNo || '',
              orderStatus: oStatus,
              emiAmount: statusRes.data.emiAmount || '',
              orderValue: statusRes.data.orderValue || '',
              posOrderId: orderId
            });
            setSnapmintProcessing(false);
          } else if (oStatus === 'CANCELLED' || oStatus === 'VOIDED' || oStatus === 'REFUNDED') {
            clearInterval(snapmintPollRef.current);
            snapmintPollRef.current = null;
            if (snapmintCountdownRef.current) { clearInterval(snapmintCountdownRef.current); snapmintCountdownRef.current = null; }
            setSnapmintCountdown(0);
            setSnapmintStatus('FAILED');
            setSnapmintError(`Payment ${oStatus.toLowerCase()} by customer`);
            setSnapmintProcessing(false);
          }
          // PENDING_STATUS / NOT_FOUND — keep polling
        } catch (pollErr) {
          console.error('Snapmint poll error:', pollErr);
        }

        if (pollCount >= maxPolls) {
          clearInterval(snapmintPollRef.current);
          snapmintPollRef.current = null;
          if (snapmintCountdownRef.current) { clearInterval(snapmintCountdownRef.current); snapmintCountdownRef.current = null; }
          setSnapmintCountdown(0);
          setSnapmintStatus('FAILED');
          setSnapmintError('Payment timed out after 10 minutes. Please try again.');
          useActivityLogStore.getState().logPaymentTimeout('snapmint', cart.total);
          setSnapmintProcessing(false);
        }
      }, 10000);

    } catch (error) {
      console.error('Snapmint payment error:', error);
      setSnapmintStatus('FAILED');
      setSnapmintError(error.response?.data?.error || 'Failed to initiate Snapmint payment');
      setSnapmintProcessing(false);
    }
  };

  // Cancel ongoing Snapmint polling
  const cancelSnapmintPayment = () => {
    if (snapmintPollRef.current) { clearInterval(snapmintPollRef.current); snapmintPollRef.current = null; }
    if (snapmintCountdownRef.current) { clearInterval(snapmintCountdownRef.current); snapmintCountdownRef.current = null; }
    setSnapmintCountdown(0);
    setSnapmintStatus('');
    setSnapmintError('');
    setSnapmintQr('');
    setSnapmintUrl('');
    setSnapmintOrderId('');
    setSnapmintDetails(null);
    setSnapmintProcessing(false);
    setTransactionId('');
  };

  // Initiate Ezetap payment and poll for result
  const handleEzetapPayment = async () => {
    if (ezetapSendingRef.current) return; // Prevent duplicate terminal requests
    if (!validatePrePayment()) return;
    if (!(await revalidateBeforePayment())) return; // re-check coupons before charging on terminal
    if (!selectedCustomer) {
      alert('Please select a customer before initiating terminal payment');
      return;
    }
    ezetapSendingRef.current = true;
    setEzetapProcessing(true);
    setEzetapStatus('INITIATED');
    setEzetapError('');
    setTransactionId('');
    const refNumber = `POS-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setEzetapRefNumber(refNumber);
    lastEzetapAttemptRef.current = { kind: 'single', stepNum: 0, mode: paymentMethod === 'upi' ? 'UPI' : 'CARD', amount: parseFloat(cart.total) };
    useActivityLogStore.getState().logPaymentInitiated(paymentMethod, cart.total, refNumber);

    try {
      const payRes = await api.post('/checkout/ezetap/pay', {
        amount: parseFloat(cart.total),
        externalRefNumber: refNumber,
        customerName: selectedCustomer ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() : '',
        customerMobileNumber: selectedCustomer?.phone || '',
        description: `POS Order - ${cart.items.length} item(s)`,
        paymentMode: paymentMethod === 'upi' ? 'UPI' : paymentMethod === 'card' ? 'CARD' : 'ALL',
        ezetapUsername: user?.ezetapUsername || '',
        ezetapDeviceId: user?.ezetapDeviceId || ''
      });

      if (!payRes.data.success || !payRes.data.p2pRequestId) {
        setEzetapStatus('FAILED');
        setEzetapError(payRes.data.error || 'Failed to initiate payment');
        setEzetapProcessing(false);
        ezetapSendingRef.current = false;
        return;
      }

      const requestId = payRes.data.p2pRequestId;
      ezetapRequestIdRef.current = requestId;

      setEzetapStatus('POLLING');

      // UPI gets 6 minutes, Card gets 5 minutes (EZETAP_TIMEOUT_SECONDS)
      const isUpi = paymentMethod === 'upi';
      const totalSeconds = isUpi ? EZETAP_TIMEOUT_SECONDS.UPI : EZETAP_TIMEOUT_SECONDS.CARD;
      const maxPolls = Math.ceil(totalSeconds / 10); // one poll every 10 s
      let pollCount = 0;

      // Start countdown timer
      setEzetapCountdown(totalSeconds);
      if (ezetapCountdownRef.current) clearInterval(ezetapCountdownRef.current);
      ezetapCountdownRef.current = setInterval(() => {
        setEzetapCountdown(prev => {
          if (prev <= 1) {
            clearInterval(ezetapCountdownRef.current);
            ezetapCountdownRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      ezetapPollRef.current = setInterval(async () => {
        pollCount++;
        try {
          const statusRes = await api.post('/checkout/ezetap/status', { p2pRequestId: requestId });
          const pStatus = statusRes.data.paymentStatus;

          if (pStatus === 'SUCCESS' || pStatus === 'AUTHORIZED') {
            clearInterval(ezetapPollRef.current);
            ezetapPollRef.current = null;
            if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
            setEzetapCountdown(0);
            setEzetapStatus('SUCCESS');
            setTransactionId(statusRes.data.transactionId || requestId);
            setEzetapDetails({
              txnId: statusRes.data.transactionId || '',
              p2pRequestId: statusRes.data.p2pRequestId || requestId,
              receiptUrl: statusRes.data.receiptUrl || '',
              paymentMode: statusRes.data.paymentMode || '',
              externalRefNumber: refNumber,
              raw: statusRes.data.raw || {}
            });
            useActivityLogStore.getState().logPaymentSuccess(paymentMethod, statusRes.data.transactionId || requestId, refNumber);
            setEzetapProcessing(false);
          } else if (pStatus === 'FAILED' || pStatus === 'CANCELLED' || pStatus === 'DECLINED') {
            clearInterval(ezetapPollRef.current);
            ezetapPollRef.current = null;
            if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
            setEzetapCountdown(0);
            setEzetapStatus('FAILED');
            setEzetapError(`Payment ${pStatus.toLowerCase()} on terminal`);
            if (pStatus === 'CANCELLED') {
              useActivityLogStore.getState().logPaymentCancelled(paymentMethod, cart.total, refNumber);
            } else {
              useActivityLogStore.getState().logPaymentFailed(paymentMethod, `Payment ${pStatus.toLowerCase()} on terminal`, refNumber);
            }
            setEzetapProcessing(false);
            ezetapSendingRef.current = false;
          }
          // else still pending, continue polling
        } catch (pollErr) {
          console.error('Ezetap poll error:', pollErr);
        }

        if (pollCount >= maxPolls) {
          clearInterval(ezetapPollRef.current);
          ezetapPollRef.current = null;
          if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
          setEzetapCountdown(0);
          // Final status check BEFORE cancelling: a payment can complete at the
          // last moment, and cancelling buries a completed device transaction
          // (P2P_DEVICE_TXN_DONE — the wrapper expires while the charge succeeded).
          let lastData = null, lastRaw = {};
          try {
            const fr = await api.post('/checkout/ezetap/status', { p2pRequestId: requestId, ezetapUsername: user?.ezetapUsername || '' });
            lastData = fr.data; lastRaw = fr.data.raw || {};
          } catch (e) { /* fall through to timeout handling */ }
          if (lastData && (lastData.paymentStatus === 'SUCCESS' || lastData.paymentStatus === 'AUTHORIZED')) {
            setEzetapStatus('SUCCESS');
            setTransactionId(lastData.transactionId || requestId);
            setEzetapDetails({
              txnId: lastData.transactionId || '',
              p2pRequestId: lastData.p2pRequestId || requestId,
              receiptUrl: lastData.receiptUrl || '',
              paymentMode: lastData.paymentMode || '',
              externalRefNumber: refNumber,
              raw: lastRaw
            });
            useActivityLogStore.getState().logPaymentSuccess(paymentMethod, lastData.transactionId || requestId, refNumber);
            ezetapRequestIdRef.current = null;
            setEzetapProcessing(false);
            ezetapSendingRef.current = false;
            return;
          }
          // ROLLBACK(9fe12fe) 3 Sep 2026: device-txn-done handling disabled pending debug.
          // (Briefly re-enabled for Worli 1 Oct 2026; switched off again the same day.)
          // const deviceDone = lastRaw.realCode === 'P2P_DEVICE_TXN_DONE';
          const deviceDone = false;
          // Auto-cancel the pending payment on the terminal — but never when the
          // device reports a completed transaction
          if (!deviceDone && ezetapRequestIdRef.current) {
            try {
              await api.post('/checkout/ezetap/cancel', { p2pRequestId: ezetapRequestIdRef.current, ezetapUsername: user?.ezetapUsername || '' });
            } catch (cancelErr) {
              console.error('Auto-cancel error:', cancelErr);
            }
          }
          ezetapRequestIdRef.current = null;
          setEzetapStatus('FAILED');
          setEzetapError(deviceDone
            ? 'Terminal reports a completed transaction — if the customer has a charge-slip, enter its Ref # below.'
            : 'Payment timed out — automatically cancelled. Please try again.');
          useActivityLogStore.getState().logPaymentTimeout(paymentMethod, cart.total, refNumber);
          setEzetapProcessing(false);
          ezetapSendingRef.current = false;
        }
      }, 10000);

    } catch (error) {
      console.error('Ezetap payment error:', error);
      setEzetapStatus('FAILED');
      setEzetapError(error.response?.data?.error || 'Failed to connect to payment terminal');
      setEzetapProcessing(false);
      ezetapSendingRef.current = false;
    }
  };

  // ─── Split Ezetap: 2-step terminal flow ───────────────────────────────────
  const handleSplitEzetapStep = async (stepNum, amount, mode) => {
    if (ezetapSendingRef.current) return; // Prevent duplicate terminal requests
    if (stepNum === 1 && !validatePrePayment()) return;
    if (stepNum === 1 && !(await revalidateBeforePayment())) return; // re-check coupons before first charge
    if (!selectedCustomer) {
      alert('Please select a customer before initiating terminal payment');
      return;
    }
    ezetapSendingRef.current = true;
    setEzetapProcessing(true);
    setEzetapStatus('INITIATED');
    setEzetapError('');
    setSplitStep(stepNum);
    lastEzetapAttemptRef.current = { kind: 'split', stepNum, mode, amount: parseFloat(amount) };

    const refNumber = `POS-SPLIT${stepNum}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    try {
      const payRes = await api.post('/checkout/ezetap/pay', {
        amount: parseFloat(amount),
        externalRefNumber: refNumber,
        customerName: selectedCustomer ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() : '',
        customerMobileNumber: selectedCustomer?.phone || '',
        description: `POS Split Step ${stepNum} - ${mode}`,
        paymentMode: mode,
        ezetapUsername: user?.ezetapUsername || '',
        ezetapDeviceId: user?.ezetapDeviceId || ''
      });

      if (!payRes.data.success || !payRes.data.p2pRequestId) {
        setEzetapStatus('FAILED');
        setEzetapError(payRes.data.error || `Failed to initiate ${mode.toLowerCase()} payment`);
        setEzetapProcessing(false);
        ezetapSendingRef.current = false;
        return;
      }

      const requestId = payRes.data.p2pRequestId;
      ezetapRequestIdRef.current = requestId;
      setEzetapStatus('POLLING');

      const isUpi = mode === 'UPI';
      const totalSeconds = isUpi ? EZETAP_TIMEOUT_SECONDS.UPI : EZETAP_TIMEOUT_SECONDS.CARD;
      const maxPolls = Math.ceil(totalSeconds / 10); // one poll every 10 s
      let pollCount = 0;

      setEzetapCountdown(totalSeconds);
      if (ezetapCountdownRef.current) clearInterval(ezetapCountdownRef.current);
      ezetapCountdownRef.current = setInterval(() => {
        setEzetapCountdown(prev => {
          if (prev <= 1) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; return 0; }
          return prev - 1;
        });
      }, 1000);

      ezetapPollRef.current = setInterval(async () => {
        pollCount++;
        try {
          const statusRes = await api.post('/checkout/ezetap/status', { p2pRequestId: requestId, ezetapUsername: user?.ezetapUsername || '' });
          const pStatus = statusRes.data.paymentStatus;

          if (pStatus === 'SUCCESS' || pStatus === 'AUTHORIZED') {
            clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
            if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
            setEzetapCountdown(0);

            const detail = {
              txnId: statusRes.data.transactionId || '',
              p2pRequestId: statusRes.data.p2pRequestId || requestId,
              receiptUrl: statusRes.data.receiptUrl || '',
              paymentMode: statusRes.data.paymentMode || mode,
              externalRefNumber: refNumber,
              amount: parseFloat(amount),
              raw: statusRes.data.raw || {}
            };

            if (stepNum === 1) {
              // Cash done, ready for step 2
              setSplitEzetapDetails([detail]);
              setSplitStep(2); // signal card/UPI step ready
              setEzetapStatus('');
              setEzetapProcessing(false);
              ezetapSendingRef.current = false; // Allow step 2 to send
            } else {
              // Step 2 done — both complete
              setSplitEzetapDetails(prev => [...prev, detail]);
              setSplitStep(3);
              setEzetapStatus('SUCCESS');
              // Combine txnIds for the transactionId field
              setTransactionId(statusRes.data.transactionId || requestId);
              setEzetapDetails({
                txnId: statusRes.data.transactionId || '',
                p2pRequestId: statusRes.data.p2pRequestId || requestId,
                receiptUrl: statusRes.data.receiptUrl || '',
                paymentMode: statusRes.data.paymentMode || mode,
                externalRefNumber: refNumber,
                raw: statusRes.data.raw || {}
              });
              setEzetapProcessing(false);
            }
          } else if (pStatus === 'FAILED' || pStatus === 'CANCELLED' || pStatus === 'DECLINED') {
            clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
            if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
            setEzetapCountdown(0);
            setEzetapStatus('FAILED');
            setEzetapError(`Step ${stepNum} ${mode.toLowerCase()} payment ${pStatus.toLowerCase()} on terminal`);
            setEzetapProcessing(false);
            ezetapSendingRef.current = false;
          }
        } catch (pollErr) {
          console.error('Split Ezetap poll error:', pollErr);
        }

        if (pollCount >= maxPolls) {
          clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
          if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
          setEzetapCountdown(0);
          // Final check before cancel (see single-flow comment)
          let lastData = null, lastRaw = {};
          try {
            const fr = await api.post('/checkout/ezetap/status', { p2pRequestId: requestId, ezetapUsername: user?.ezetapUsername || '' });
            lastData = fr.data; lastRaw = fr.data.raw || {};
          } catch (e) {}
          if (lastData && (lastData.paymentStatus === 'SUCCESS' || lastData.paymentStatus === 'AUTHORIZED')) {
            const detail = {
              txnId: lastData.transactionId || '',
              p2pRequestId: lastData.p2pRequestId || requestId,
              receiptUrl: lastData.receiptUrl || '',
              paymentMode: lastData.paymentMode || mode,
              externalRefNumber: refNumber,
              amount: parseFloat(amount),
              raw: lastRaw
            };
            if (stepNum === 1) {
              setSplitEzetapDetails([detail]);
              setSplitStep(2);
              setEzetapStatus('');
            } else {
              setSplitEzetapDetails(prev => [...prev, detail]);
              setSplitStep(3);
              setEzetapStatus('SUCCESS');
              setTransactionId(lastData.transactionId || requestId);
              setEzetapDetails(detail);
            }
            ezetapRequestIdRef.current = null;
            setEzetapProcessing(false);
            ezetapSendingRef.current = false;
            return;
          }
          // ROLLBACK(9fe12fe) 3 Sep 2026: device-txn-done handling disabled pending debug.
          // (Briefly re-enabled for Worli 1 Oct 2026; switched off again the same day.)
          // const deviceDone = lastRaw.realCode === 'P2P_DEVICE_TXN_DONE';
          const deviceDone = false;
          if (!deviceDone && ezetapRequestIdRef.current) {
            try { await api.post('/checkout/ezetap/cancel', { p2pRequestId: ezetapRequestIdRef.current, ezetapUsername: user?.ezetapUsername || '' }); } catch (e) {}
          }
          ezetapRequestIdRef.current = null;
          setEzetapStatus('FAILED');
          setEzetapError(deviceDone
            ? `Step ${stepNum}: terminal reports a completed transaction — enter the charge-slip Ref # below.`
            : `Step ${stepNum} ${mode.toLowerCase()} payment timed out — automatically cancelled.`);
          setEzetapProcessing(false);
          ezetapSendingRef.current = false;
        }
      }, 10000);
    } catch (error) {
      console.error('Split Ezetap error:', error);
      setEzetapStatus('FAILED');
      setEzetapError(error.response?.data?.error || 'Failed to connect to payment terminal');
      setEzetapProcessing(false);
      ezetapSendingRef.current = false;
    }
  };

  // ─── Manual txn-id recovery (terminal timed out but payment actually went through) ───
  // Staff enters the Transaction ID from the customer's charge-slip; the server
  // re-verifies it with Razorpay against the attempts this POS initiated (successful
  // status + matching amount + not already used on any order) and claims it.
  const handleVerifyRecoveredTxn = async () => {
    const ctx = lastEzetapAttemptRef.current;
    const entered = recoverTxnInput.trim();
    if (!ctx || !entered || recovering) return;
    setRecovering(true);
    setRecoverError('');
    try {
      const res = await api.post('/checkout/ezetap/verify-txn', {
        txnId: entered,
        expectedAmount: ctx.amount,
        locationId,
        ezetapUsername: user?.ezetapUsername || ''
      });
      const v = res.data;
      const detail = {
        txnId: v.txnId,
        p2pRequestId: v.p2pRequestId || '',
        receiptUrl: v.receiptUrl || '',
        paymentMode: v.paymentMode || ctx.mode,
        externalRefNumber: v.externalRefNumber || '',
        amount: ctx.amount,
        recovered: true,
        raw: {}
      };
      // Replicate the exact state transitions of a successful poll for this flow/leg
      if (ctx.kind === 'single') {
        setEzetapStatus('SUCCESS');
        setTransactionId(v.txnId);
        setEzetapDetails(detail);
      } else if (ctx.kind === 'split') {
        if (ctx.stepNum === 1) {
          setSplitEzetapDetails([detail]);
          setSplitStep(2);
          setEzetapStatus('');
        } else {
          setSplitEzetapDetails(prev => [...prev, detail]);
          setSplitStep(3);
          setEzetapStatus('SUCCESS');
          setTransactionId(v.txnId);
          setEzetapDetails(detail);
        }
      } else if (ctx.kind === 'splitUpiCard') {
        if (ctx.stepNum === 1) {
          setSplitUpiCardDetails([detail]);
          setSplitUpiCardStep(2);
          setEzetapStatus('');
        } else {
          setSplitUpiCardDetails(prev => [...prev, detail]);
          setSplitUpiCardStep(3);
          setEzetapStatus('SUCCESS');
          setTransactionId(v.txnId);
          setEzetapDetails(detail);
        }
      }
      setEzetapError('');
      setRecoverTxnInput('');
      setSlipEntryOpen(false);
      ezetapSendingRef.current = false;
      useActivityLogStore.getState().logPaymentSuccess(paymentMethod, v.txnId, v.externalRefNumber || 'txn-recovery');
    } catch (e) {
      setRecoverError(e.response?.data?.error || 'Verification failed — please try again');
    }
    setRecovering(false);
  };

  const renderTxnRecovery = () => (
    <div style={{ marginTop: '10px', padding: '10px', background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: '10px' }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', marginBottom: '6px' }}>
        💡 Payment actually completed? Enter the <u>Ref #</u> from the charge-slip (starts with POS-…):
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          type="text"
          value={recoverTxnInput}
          onChange={e => { setRecoverTxnInput(e.target.value); setRecoverError(''); }}
          placeholder="Ref # (POS-…) or RRN from slip"
          disabled={recovering}
          style={{ flex: 1, padding: '8px 10px', border: '1.5px solid #f59e0b', borderRadius: '8px', fontSize: '13px', fontWeight: '600', boxSizing: 'border-box' }}
        />
        <button
          onClick={handleVerifyRecoveredTxn}
          disabled={recovering || !recoverTxnInput.trim()}
          style={{ padding: '8px 14px', background: recovering || !recoverTxnInput.trim() ? '#d1d5db' : '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: recovering || !recoverTxnInput.trim() ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
        >
          {recovering ? 'Verifying…' : 'Verify & Accept'}
        </button>
      </div>
      {recoverError && (
        <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: '600', color: '#b91c1c' }}>{recoverError}</div>
      )}
      <div style={{ marginTop: '6px', fontSize: '11px', color: '#92400e' }}>
        The Ref # is checked with Razorpay for a successful payment of ₹{(lastEzetapAttemptRef.current?.amount || 0).toFixed(2)} and locked to this order so it cannot be reused.
      </div>
    </div>
  );

  // Toggle link shown BEFORE a terminal payment is initiated: place the order
  // against a same-day charge-slip directly (no terminal round-trip). Opens the
  // same recovery panel; ctx tells the verify handler which flow/leg/amount.
  // ROLLBACK(9fe12fe): its 4 call sites are commented out pending debug.
  // eslint-disable-next-line no-unused-vars
  const renderSlipEntryOption = (ctx) => (
    slipEntryOpen ? (
      <div>
        {renderTxnRecovery()}
        <button
          onClick={() => { setSlipEntryOpen(false); setRecoverTxnInput(''); setRecoverError(''); }}
          style={{ marginTop: '6px', padding: '4px 10px', background: 'transparent', border: 'none', color: '#6b7280', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Cancel slip entry
        </button>
      </div>
    ) : (
      <button
        onClick={() => { lastEzetapAttemptRef.current = ctx; setSlipEntryOpen(true); setRecoverTxnInput(''); setRecoverError(''); }}
        style={{ marginTop: '8px', width: '100%', padding: '8px', background: '#fffbeb', color: '#92400e', border: '1.5px dashed #f59e0b', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
      >
        🧾 Already paid? Place order with charge-slip Ref #
      </button>
    )
  );

  const cancelEzetapPayment = async () => {
    if (ezetapPollRef.current) {
      clearInterval(ezetapPollRef.current);
      ezetapPollRef.current = null;
    }
    if (ezetapCountdownRef.current) {
      clearInterval(ezetapCountdownRef.current);
      ezetapCountdownRef.current = null;
    }
    setEzetapCountdown(0);
    // Cancel pending payment request on the terminal
    const reqId = ezetapRequestIdRef.current;
    if (reqId) {
      try {
        await api.post('/checkout/ezetap/cancel', { p2pRequestId: reqId, ezetapUsername: user?.ezetapUsername || '' });
      } catch (err) {
        console.error('Ezetap cancel error:', err);
      }
    }
    ezetapRequestIdRef.current = null;
    ezetapSendingRef.current = false;
    setEzetapProcessing(false);
    setEzetapStatus('');
    setEzetapError('');
    setEzetapDetails(null);
    setTransactionId('');
    // Reset split state if cancelling during split flow
    if (paymentMethod === 'split') {
      setSplitStep(0);
      setSplitEzetapDetails([]);
    }
    if (paymentMethod === 'splitUpiCard') {
      setSplitUpiCardStep(0);
      setSplitUpiCardDetails([]);
    }
  };

  // ─── Split UPI+Card: 2-step terminal flow (UPI first, then Card) ─────────
  const handleSplitUpiCardStep = async (stepNum, amount, mode) => {
    if (ezetapSendingRef.current) return;
    if (stepNum === 1 && !validatePrePayment()) return;
    if (stepNum === 1 && !(await revalidateBeforePayment())) return; // re-check coupons before first charge
    if (!selectedCustomer) {
      alert('Please select a customer before initiating terminal payment');
      return;
    }
    ezetapSendingRef.current = true;
    setEzetapProcessing(true);
    setEzetapStatus('INITIATED');
    setEzetapError('');
    setSplitUpiCardStep(stepNum);
    lastEzetapAttemptRef.current = { kind: 'splitUpiCard', stepNum, mode, amount: parseFloat(amount) };

    const refNumber = `POS-SPLITUC${stepNum}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    try {
      const payRes = await api.post('/checkout/ezetap/pay', {
        amount: parseFloat(amount),
        externalRefNumber: refNumber,
        customerName: selectedCustomer ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() : '',
        customerMobileNumber: selectedCustomer?.phone || '',
        description: `POS Split UPI+Card Step ${stepNum} - ${mode}`,
        paymentMode: mode,
        ezetapUsername: user?.ezetapUsername || '',
        ezetapDeviceId: user?.ezetapDeviceId || ''
      });

      if (!payRes.data.success || !payRes.data.p2pRequestId) {
        setEzetapStatus('FAILED');
        setEzetapError(payRes.data.error || `Failed to initiate ${mode.toLowerCase()} payment`);
        setEzetapProcessing(false);
        ezetapSendingRef.current = false;
        return;
      }

      const requestId = payRes.data.p2pRequestId;
      ezetapRequestIdRef.current = requestId;
      setEzetapStatus('POLLING');

      const isUpi = mode === 'UPI';
      const totalSeconds = isUpi ? EZETAP_TIMEOUT_SECONDS.UPI : EZETAP_TIMEOUT_SECONDS.CARD;
      const maxPolls = Math.ceil(totalSeconds / 10); // one poll every 10 s
      let pollCount = 0;

      setEzetapCountdown(totalSeconds);
      if (ezetapCountdownRef.current) clearInterval(ezetapCountdownRef.current);
      ezetapCountdownRef.current = setInterval(() => {
        setEzetapCountdown(prev => {
          if (prev <= 1) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; return 0; }
          return prev - 1;
        });
      }, 1000);

      ezetapPollRef.current = setInterval(async () => {
        pollCount++;
        try {
          const statusRes = await api.post('/checkout/ezetap/status', { p2pRequestId: requestId, ezetapUsername: user?.ezetapUsername || '' });
          const pStatus = statusRes.data.paymentStatus;

          if (pStatus === 'SUCCESS' || pStatus === 'AUTHORIZED') {
            clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
            if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
            setEzetapCountdown(0);

            const detail = {
              txnId: statusRes.data.transactionId || '',
              p2pRequestId: statusRes.data.p2pRequestId || requestId,
              receiptUrl: statusRes.data.receiptUrl || '',
              paymentMode: statusRes.data.paymentMode || mode,
              externalRefNumber: refNumber,
              amount: parseFloat(amount),
              raw: statusRes.data.raw || {}
            };

            if (stepNum === 1) {
              // UPI done, ready for Card step
              setSplitUpiCardDetails([detail]);
              setSplitUpiCardStep(2);
              setEzetapStatus('');
              setEzetapProcessing(false);
              ezetapSendingRef.current = false;
            } else {
              // Step 2 (Card) done — both complete
              setSplitUpiCardDetails(prev => [...prev, detail]);
              setSplitUpiCardStep(3);
              setEzetapStatus('SUCCESS');
              setTransactionId(statusRes.data.transactionId || requestId);
              setEzetapDetails({
                txnId: statusRes.data.transactionId || '',
                p2pRequestId: statusRes.data.p2pRequestId || requestId,
                receiptUrl: statusRes.data.receiptUrl || '',
                paymentMode: statusRes.data.paymentMode || mode,
                externalRefNumber: refNumber,
                raw: statusRes.data.raw || {}
              });
              setEzetapProcessing(false);
            }
          } else if (pStatus === 'FAILED' || pStatus === 'CANCELLED' || pStatus === 'DECLINED') {
            clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
            if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
            setEzetapCountdown(0);
            setEzetapStatus('FAILED');
            setEzetapError(`Step ${stepNum} ${mode.toLowerCase()} payment ${pStatus.toLowerCase()} on terminal`);
            setEzetapProcessing(false);
            ezetapSendingRef.current = false;
          }
        } catch (pollErr) {
          console.error('Split UPI+Card poll error:', pollErr);
        }

        if (pollCount >= maxPolls) {
          clearInterval(ezetapPollRef.current); ezetapPollRef.current = null;
          if (ezetapCountdownRef.current) { clearInterval(ezetapCountdownRef.current); ezetapCountdownRef.current = null; }
          setEzetapCountdown(0);
          // Final check before cancel (see single-flow comment)
          let lastData = null, lastRaw = {};
          try {
            const fr = await api.post('/checkout/ezetap/status', { p2pRequestId: requestId, ezetapUsername: user?.ezetapUsername || '' });
            lastData = fr.data; lastRaw = fr.data.raw || {};
          } catch (e) {}
          if (lastData && (lastData.paymentStatus === 'SUCCESS' || lastData.paymentStatus === 'AUTHORIZED')) {
            const detail = {
              txnId: lastData.transactionId || '',
              p2pRequestId: lastData.p2pRequestId || requestId,
              receiptUrl: lastData.receiptUrl || '',
              paymentMode: lastData.paymentMode || mode,
              externalRefNumber: refNumber,
              amount: parseFloat(amount),
              raw: lastRaw
            };
            if (stepNum === 1) {
              setSplitUpiCardDetails([detail]);
              setSplitUpiCardStep(2);
              setEzetapStatus('');
            } else {
              setSplitUpiCardDetails(prev => [...prev, detail]);
              setSplitUpiCardStep(3);
              setEzetapStatus('SUCCESS');
              setTransactionId(lastData.transactionId || requestId);
              setEzetapDetails(detail);
            }
            ezetapRequestIdRef.current = null;
            setEzetapProcessing(false);
            ezetapSendingRef.current = false;
            return;
          }
          // ROLLBACK(9fe12fe) 3 Sep 2026: device-txn-done handling disabled pending debug.
          // (Briefly re-enabled for Worli 1 Oct 2026; switched off again the same day.)
          // const deviceDone = lastRaw.realCode === 'P2P_DEVICE_TXN_DONE';
          const deviceDone = false;
          if (!deviceDone && ezetapRequestIdRef.current) {
            try { await api.post('/checkout/ezetap/cancel', { p2pRequestId: ezetapRequestIdRef.current, ezetapUsername: user?.ezetapUsername || '' }); } catch (e) {}
          }
          ezetapRequestIdRef.current = null;
          setEzetapStatus('FAILED');
          setEzetapError(deviceDone
            ? `Step ${stepNum}: terminal reports a completed transaction — enter the charge-slip Ref # below.`
            : `Step ${stepNum} ${mode.toLowerCase()} payment timed out — automatically cancelled.`);
          setEzetapProcessing(false);
          ezetapSendingRef.current = false;
        }
      }, 10000);
    } catch (error) {
      console.error('Split UPI+Card error:', error);
      setEzetapStatus('FAILED');
      setEzetapError(error.response?.data?.error || 'Failed to connect to payment terminal');
      setEzetapProcessing(false);
      ezetapSendingRef.current = false;
    }
  };

  const handleCheckout = async () => {
    if (!locationId) {
      alert('Please select a location from the header dropdown');
      return;
    }

    if (!paymentMethod) {
      alert('Please select a payment method');
      return;
    }

    if (cart.items.length === 0) {
      alert('Cart is empty');
      return;
    }

    // For DIRECT methods (cash / district / netbanking) this is the payment-initiation point, so
    // re-validate coupons here before finalizing. Terminal methods (card/upi/split/splitUpiCard/
    // snapmint) already revalidated before charging, so they skip this (avoids a second request).
    if (['cash', 'district', 'netbanking'].includes(paymentMethod)) {
      if (!(await revalidateBeforePayment())) return;
    }

    // Validate staff selection
    if (posStaffs.length > 0 && !selectedStaff) {
      alert('Please select a staff name');
      return;
    }

    // Validate customer for store locations (mandatory) and warehouse fulfillment
    const currentLoc = allLocations.find(l => l.id?.toString() === locationId?.toString());
    const isStoreLocation = !currentLoc || currentLoc?.type?.toLowerCase() === 'store';
    if (isStoreLocation && !selectedCustomer) {
      alert('Customer selection is mandatory for store orders');
      return;
    }
    // Block checkout if customer has missing required fields
    if (selectedCustomer && getCustomerMissingFields(selectedCustomer).length > 0) {
      alert('Please update customer details (missing: ' + getCustomerMissingFields(selectedCustomer).join(', ') + ') before placing the order.');
      return;
    }
    // Block checkout if there's an unresolved customer edit error
    if (customerEditError) {
      alert('Please resolve the customer update error before placing the order.');
      return;
    }
    if (fulfillmentSource === 'warehouse' && !selectedCustomer) {
      alert('Customer details are required for warehouse fulfillment/shipping orders');
      return;
    }

    // Validate shipping address for warehouse fulfillment
    if (hasWarehouseItems || (fulfillmentSource === 'warehouse' && !isMultiLocation)) {
      if (!selectedAddressId && !showNewAddressForm) {
        alert('Please select a shipping address or add a new one');
        return;
      }
      if (showNewAddressForm || !selectedAddressId) {
        if (!shippingAddress.address1 || !shippingAddress.city || !shippingAddress.province || !shippingAddress.zip) {
          alert('Complete shipping address is required for warehouse fulfillment');
          return;
        }
      }
      // Require pincode verification only for warehouse items
      if (!pincodeVerified) {
        alert('Please verify the delivery pincode before checkout');
        return;
      }
    }
    // Address is COMPULSORY for store orders too — require a selected address or a complete new one
    // (pincode verification stays warehouse-only).
    if (fulfillmentSource === 'store' && isStoreLocation) {
      const addingNew = showNewAddressForm || customerAddresses.length === 0;
      if (!selectedAddressId && !addingNew) {
        alert('Please select a customer address or add a new one');
        return;
      }
      if (addingNew || !selectedAddressId) {
        if (!shippingAddress.address1 || !shippingAddress.city || !shippingAddress.province || !shippingAddress.zip) {
          alert('Please enter a complete address (address, city, state, pincode)');
          return;
        }
      }
    }

    // Validate terminal payment for card/UPI (cash is physical — no terminal needed)
    if ((paymentMethod === 'card' || paymentMethod === 'upi') && !transactionId.trim()) {
      alert('Please complete payment via the terminal before checkout');
      return;
    }
    // Validate Transaction ID for District by Zomato / NetBanking
    if ((paymentMethod === 'district' || paymentMethod === 'netbanking') && !transactionId.trim()) {
      alert(`Please enter the ${paymentMethod === 'district' ? 'District by Zomato' : 'NetBanking / NEFT / RTGS / Bank Transfer'} Transaction ID before checkout`);
      return;
    }
    // Transaction ID must fit the orders.transaction_id column (VARCHAR(128)); a longer value fails
    // the DB insert and the order never gets saved locally.
    if (transactionId && transactionId.trim().length > 128) {
      alert('Transaction ID cannot exceed 128 characters. Please shorten it before checkout.');
      return;
    }
    // Validate Snapmint payment completed via QR/polling
    if (paymentMethod === 'snapmint' && snapmintStatus !== 'SUCCESS') {
      alert('Please complete Snapmint EMI payment before checkout');
      return;
    }
    // Validate split payment: cash confirmed + Card/UPI terminal complete
    if (paymentMethod === 'split' && splitStep !== 3) {
      alert('Please confirm cash and complete Card/UPI payment on the terminal before checkout');
      return;
    }

    // Validate split UPI+Card: both terminal steps complete
    if (paymentMethod === 'splitUpiCard' && splitUpiCardStep !== 3) {
      alert('Please complete both UPI and Card payments on the terminal before checkout');
      return;
    }

    // Validate split payment amounts
    if (paymentMethod === 'split') {
      const cashAmt = parseFloat(splitCash) || 0;
      const cardAmt = parseFloat(splitCard) || 0;
      const total = parseFloat(cart.total);
      if (cashAmt <= 0 || cardAmt <= 0) {
        alert('Both cash and card amounts must be greater than zero for split payment');
        return;
      }
      if (cashAmt >= total) {
        alert('Cash amount cannot be equal to or greater than the invoice total. Use "Cash" payment instead.');
        return;
      }
      if (Math.abs(cashAmt + cardAmt - total) > 0.01) {
        alert(`Cash (${formatCurrency(cashAmt)}) + Card (${formatCurrency(cardAmt)}) must equal the total (${formatCurrency(total)})`);
        return;
      }
    }

    // Validate split UPI+Card amounts
    if (paymentMethod === 'splitUpiCard') {
      const upiAmt = parseFloat(splitUpiAmount) || 0;
      const cardAmt = parseFloat(splitCardAmount) || 0;
      const total = parseFloat(cart.total);
      if (upiAmt <= 0 || cardAmt <= 0) {
        alert('Both UPI and Card amounts must be greater than zero for split payment');
        return;
      }
      if (Math.abs(upiAmt + cardAmt - total) > 0.01) {
        alert(`UPI (${formatCurrency(upiAmt)}) + Card (${formatCurrency(cardAmt)}) must equal the total (${formatCurrency(total)})`);
        return;
      }
    }

    // Validate GSTIN if GST billing is enabled
    if (gstBilling) {
      if (!selectedCustomer) {
        alert('Customer selection is mandatory for GST Billing');
        return;
      }
      if (!gstin.trim()) {
        alert('Please enter GST Number for GST Billing');
        return;
      }
      if (!isValidGST(gstin.trim().toUpperCase())) {
        alert('Invalid GSTIN format. Please enter a valid 15-character GSTIN (e.g. 22AAAAA0000A1Z5)');
        return;
      }
      if (!gstCompanyName.trim()) {
        alert('Please enter Company Name for GST Billing');
        return;
      }
      // Shipping address is required when GST is enabled (for all order types)
      const effShippingAddr = selectedAddressId
        ? customerAddresses.find(a => a.id === selectedAddressId)
        : shippingAddress;
      if (!effShippingAddr || !effShippingAddr.address1?.trim() || !effShippingAddr.city?.trim() || !effShippingAddr.zip?.trim()) {
        alert('Shipping Address is required for GST Billing. Please select or add a shipping address with Address, City and Pincode.');
        return;
      }
      // Billing address is required when GST is enabled (for all order types)
      const effBillingAddr = diffBilling
        ? (selectedBillingAddressId ? customerAddresses.find(a => a.id === selectedBillingAddressId) : billingAddress)
        : effShippingAddr;
      if (!effBillingAddr || !effBillingAddr.address1?.trim() || !effBillingAddr.city?.trim() || !effBillingAddr.zip?.trim()) {
        alert('Billing Address is required for GST Billing. Please provide Address, City and Pincode.');
        return;
      }
    }

    // Validate billing address if different billing is enabled (without GST)
    if (diffBilling && !gstBilling) {
      if (!billingAddress.address1?.trim() || !billingAddress.city?.trim() || !billingAddress.zip?.trim()) {
        alert('Please complete the billing address (Address, City, Pincode are required)');
        return;
      }
    }

    setProcessing(true);

    try {
      const checkoutData = {
        locationId: effectiveLocationId,
        paymentMethod,
        fulfillmentSource,
        notes,
        cartItems: cart.items.map(item => ({
          variantId: item.variantId,
          productId: item.productId,
          title: item.title,
          price: item.price,
          quantity: item.quantity,
          lineTotal: item.lineTotal,
          sku: item.sku,
          image: item.image,
          customAttributes: item.customAttributes || [],
          personalizationFee: item.personalizationFee || 0,
          inventoryQuantity: item.inventoryQuantity || 0,
          inventoryPolicy: item.inventoryPolicy || null,
          inventoryItemId: item.inventoryItemId || null,
          locationType: item.locationType || 'store',
          locationId: item.locationId || null,
          locationName: item.locationName || ''
        })),
        cartDiscounts: cart.discounts || [],
        cartGiftCards: cart.giftCards || [],
        cartTotal: cart.total,
        cartSubtotal: cart.subtotal,
        cartRoundOff: cart.roundOff,
        splitPayment: paymentMethod === 'split' ? {
          cash: parseFloat(splitCash),
          card: parseFloat(splitCard)
        } : paymentMethod === 'splitUpiCard' ? {
          upi: parseFloat(splitUpiAmount),
          card: parseFloat(splitCardAmount)
        } : null,
        sendReceipt: sendReceipt && !!selectedCustomer?.email,
        gstin: gstBilling && gstin.trim() ? gstin.trim().toUpperCase() : null,
        gstCompanyName: gstBilling && gstCompanyName.trim() ? gstCompanyName.trim() : null,
        billingAddress: diffBilling && billingAddress.address1 ? billingAddress : null,
        staffName: selectedStaff || null,
        transactionId: (paymentMethod === 'card' || paymentMethod === 'upi' || paymentMethod === 'split' || paymentMethod === 'splitUpiCard' || paymentMethod === 'district' || paymentMethod === 'snapmint' || paymentMethod === 'netbanking') ? String(transactionId || '').trim() : null,
        ezetapDetails: paymentMethod === 'split' && splitEzetapDetails.length > 0
          ? { splitPayments: splitEzetapDetails }
          : paymentMethod === 'splitUpiCard' && splitUpiCardDetails.length > 0
          ? { splitPayments: splitUpiCardDetails }
          : (paymentMethod === 'card' || paymentMethod === 'upi') && ezetapDetails ? ezetapDetails : null,
        snapmintDetails: paymentMethod === 'snapmint' && snapmintDetails ? snapmintDetails : null,
        customer: selectedCustomer ? {
          id: selectedCustomer.id,
          email: selectedCustomer.email,
          firstName: selectedCustomer.firstName,
          lastName: selectedCustomer.lastName,
          phone: selectedCustomer.phone
        } : null,
        shippingAddress: (fulfillmentSource === 'warehouse' || (selectedCustomer && shippingAddress && shippingAddress.address1)) ? shippingAddress : null,
        deliveryTimelines: hasWarehouseItems && pincodeResults ? (() => {
          // Key by SKU (not array index) so each line item gets ITS OWN date at checkout —
          // index-based mapping broke when items are grouped/re-indexed per location (e.g. the
          // warehouse group could pick up a different item's date).
          const timelines = {};
          pincodeResults.forEach((r) => {
            if (r.deliverable && r.deliveryDate && r.sku) {
              const d = new Date(r.deliveryDate);
              const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              timelines[r.sku] = `Delivery by ${d.getDate()} ${months[d.getMonth()]}`;
            }
          });
          return timelines;
        })() : null
      };

      console.log('[Checkout] Sending checkout request:', JSON.stringify({
        paymentMethod: checkoutData.paymentMethod,
        locationId: checkoutData.locationId,
        itemCount: checkoutData.cartItems?.length,
        cartTotal: checkoutData.cartTotal,
        hasCustomer: !!checkoutData.customer,
        hasSnapmintDetails: !!checkoutData.snapmintDetails,
        transactionId: checkoutData.transactionId,
        snapmintDetails: checkoutData.snapmintDetails
      }));

      const response = await api.post('/checkout', checkoutData);
      console.log('[Checkout] Response:', JSON.stringify(response.data));

      if (response.data.success) {
        orderCompletedRef.current = true; // block any further draft writes
        clearCheckoutDraft(); // order completed → drop the saved customer/staff/payment/address draft
        const orderNum = response.data.order?.order_number || response.data.orders?.[0]?.order_number;
        useActivityLogStore.getState().logOrderPlaced(orderNum, cart.total, paymentMethod, fulfillmentSource);
        if (response.data.orders && response.data.orders.length > 1) {
          // Multi-order response
          onComplete({ ...response.data.order, orders: response.data.orders, multiOrder: true });
        } else {
          onComplete(response.data.order);
        }
      } else {
        console.error('[Checkout] Server returned non-success:', response.status, response.data);
        alert(response.data?.error || response.data?.message || `Checkout failed (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Checkout error:', error.response?.status, error.response?.data || error.message || error);
      // A coupon's settings changed since it was applied — server rejected the stale total (checked
      // BEFORE GST). Refresh the cart, re-enable the coupon section, and show the change; don't alert.
      const cd = error.response?.data;
      if (cd?.discountChanged) {
        if (cd.cart) useCartStore.setState({ cart: cd.cart });
        resetPaymentPhase();
        setCouponsExpanded(true);
        setDiscountToast({ type: 'error', message: cd.error || 'A coupon changed — please review the updated total before checkout.' });
        return;
      }
      useActivityLogStore.getState().logPaymentFailed(paymentMethod, error.response?.data?.error || error.message);
      if (error.response?.data?.outOfStockItems) {
        const items = error.response.data.outOfStockItems;
        const itemList = items.map(i => `• ${i.title} — available: ${i.available}, requested: ${i.requested}`).join('\n');
        alert(`Cannot place order — out of stock at this location:\n\n${itemList}\n\nPlease remove or reduce quantity of these items.`);
      } else {
        const errMsg = error.response?.data?.error || error.response?.data?.details || error.message || 'Checkout failed';
        const statusCode = error.response?.status ? ` (HTTP ${error.response.status})` : '';
        alert(typeof errMsg === 'string' ? errMsg + statusCode : JSON.stringify(errMsg));
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 10, 5, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'checkoutFadeIn 0.2s ease-out'
    }}>
      <style>{`
        @keyframes checkoutFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes checkoutSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes couponSpin { to { transform: rotate(360deg); } }
        .checkout-scrollbar::-webkit-scrollbar { width: 5px; }
        .checkout-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .checkout-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .checkout-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .checkout-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.1) !important; outline: none; }
        .checkout-btn-primary { transition: all 0.2s ease; }
        .checkout-btn-primary:hover:not(:disabled) { filter: brightness(1.05); transform: translateY(-1px); }
        .checkout-btn-primary:active:not(:disabled) { transform: translateY(0); }
        .checkout-section-card { border-radius: 14px; padding: 14px; margin-bottom: 16px; transition: box-shadow 0.2s; }
        .checkout-section-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.06); }
      `}</style>
      <div style={{
        background: '#ffffff',
        
        
        borderRadius: '14px',
        maxWidth: '960px',
        width: '95%',
        maxHeight: '92vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(61,43,31,0.12), 0 8px 24px rgba(61,43,31,0.06), 0 0 0 1px #fdf8f3',
        animation: 'checkoutSlideUp 0.25s ease-out'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0', flexShrink: 0 }}>
          <div style={{ width: '100%', padding: '14px 24px', background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#f8f4ef', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #f0e6da' }}>
                <span style={{ fontSize: '17px' }}>🛒</span>
              </div>
              <div>
                <h2 style={{ fontSize: '17px', fontWeight: '700', color: '#f8fafc', margin: 0, letterSpacing: '-0.2px' }}>Checkout</h2>
                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>{cart.items.length} item{cart.items.length !== 1 ? 's' : ''} · {formatCurrency(cart.total)}</span>
              </div>
            </div>
            <button
              onClick={() => {
                // Block close if terminal payment was captured but order not yet confirmed
                // Closing here would reset all state and allow re-sending to terminal on reopen
                if ((ezetapStatus === 'SUCCESS' || (paymentMethod === 'split' && splitStep === 3)) && !processing) {
                  alert('⚠️ Terminal payment has been captured.\n\nPlease complete the order using "Complete Purchase" before closing.\n\nClosing now may cause a duplicate charge if the terminal is used again.');
                  return;
                }
                if (ezetapStatus === 'POLLING' || ezetapStatus === 'RECONCILING') {
                  alert('⚠️ Payment is being processed on the terminal.\n\nPlease wait for it to complete or press Cancel first.\n\nClosing now may result in payment collected without an order being created.');
                  return;
                }
                onClose();
              }}
              style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.08)', border: `1px solid ${(ezetapStatus === 'SUCCESS' || (paymentMethod === 'split' && splitStep === 3)) ? 'rgba(252,165,165,0.5)' : 'rgba(255,255,255,0.15)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#cbd5e1', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#f8fafc'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#cbd5e1'; }}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Two-column body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* LEFT — Order Summary (sticky) */}
          <div ref={leftColRef} className="checkout-scrollbar" style={{ width: '330px', flexShrink: 0, borderRight: '1px solid #e2e8f0', overflow: 'auto', padding: '20px 16px', background: '#f8fafc' }}>
            {addYFor && <BxgyAddYModal code={addYFor.code} needQty={addYFor.needQty} onClose={() => setAddYFor(null)} />}
            {bxgyPrompt && (
              <div onClick={() => setBxgyPrompt(null)} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', padding: '20px 22px', width: '360px', maxWidth: '90vw', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', borderTop: '4px solid #f59e0b' }}>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#92400e', marginBottom: '6px' }}>🎁 {bxgyPrompt.code} — offer unlocked</div>
                  <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>{bxgyPrompt.hint}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>The coupon stays applied; the discount appears as soon as the item is in the cart.</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
                    <button onClick={() => setBxgyPrompt(null)} style={{ padding: '7px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Later</button>
                    <button onClick={() => { setAddYFor({ code: bxgyPrompt.code, needQty: bxgyPrompt.needQty }); setBxgyPrompt(null); }} style={{ padding: '7px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>＋ Add offer item</button>
                  </div>
                </div>
              </div>
            )}
            {discountToast && (
              <div style={{
                position: 'sticky', top: 0, zIndex: 30, marginBottom: '12px',
                padding: '10px 12px', borderRadius: '10px', fontSize: '12px', fontWeight: '700',
                background: discountToast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                color: discountToast.type === 'error' ? '#dc2626' : '#16a34a',
                border: `1px solid ${discountToast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)', animation: 'fadeIn 0.2s ease'
              }}>
                {discountToast.type === 'error' ? '⚠️' : '✅'} {discountToast.message}
              </div>
            )}
            {/* BXGY offers this cart unlocks but that aren't applied — same cards as the cart panel */}
            <BxgyUnlockedCards offers={bxgyOffers} autoErrors={bxgyAutoErrors} dismissed={bxgyDismissed} compact disabled={discountsLocked}
              onAdd={(o) => setAddYFor({ code: o.code, needQty: o.needQty })}
              onApply={(o) => applyDiscount(o.code, localStorage.getItem('selectedLocation')).then(r => {
                setDiscountToast(r.success ? { type: 'success', message: r.autoAdded ? r.message : `${o.code} applied!` } : { type: 'error', message: r.error || 'Could not apply' });
                setTimeout(() => setDiscountToast(null), 3000);
              })} />
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '18px', height: '18px', borderRadius: '10px', background: '#475569', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'white' }}>📋</span>
              Order Summary
              {isMultiLocation && <span style={{ fontSize: '10px', color: '#d97706', display: 'block', marginTop: '4px', letterSpacing: '0.3px', fontWeight: '600' }}>⚡ {locationIds.length} locations — {locationIds.length} orders will be created</span>}
            </div>
            {(() => {
              if (isMultiLocation) {
                // MULTI-LOCATION (store + warehouse): partition-first. Within EACH location group:
                //  • items without a scoped coupon render plain;
                //  • each scoped coupon gets ONE block with the items only it covers (1 or many coupons alike);
                //  • an item covered by 2+ coupons renders once with a savings line per coupon (renderItemLine).
                // Coupon removal for multi-location happens in the Applied Coupons list below (so the
                // blocks/lines here are display-only, no ✕).
                const scoped = (cart.discounts || []).filter(d => !isOrderLevel(d));
                const groups = {};
                (cart.items || []).forEach((it, idx) => {
                  const locKey = it.locationId || locationId;
                  if (!groups[locKey]) groups[locKey] = { name: it.locationName || '', type: it.locationType || 'store', items: [] };
                  groups[locKey].items.push({ item: it, idx });
                });
                return (
                  <>
                    {Object.keys(groups).map(locKey => {
                      const g = groups[locKey];
                      const groupScoped = scoped.filter(d => g.items.some(({ item }) => couponQualifiesItem(d, item)));
                      return (
                        <div key={locKey}>
                          <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', padding: '4px 0 2px', marginTop: '4px', borderBottom: '1px dashed #d1d5db', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: g.type === 'warehouse' ? '#f59e0b' : '#22c55e', display: 'inline-block' }}></span>
                            {g.name} <span style={{ color: '#9ca3af', fontWeight: '400' }}>({g.type})</span>
                          </div>
                          {(() => {
                            // One block PER coupon with the items that only that coupon covers (same as the
                            // single-coupon case). An item covered by 2+ coupons (e.g. a product coupon and a
                            // BXGY on the same line) is shown once, with a savings line per coupon.
                            const couponsOn = ({ item }) => groupScoped.filter(d => couponQualifiesItem(d, item)).length;
                            const plainItems = g.items.filter(x => couponsOn(x) === 0);
                            const sharedItems = g.items.filter(x => couponsOn(x) > 1);
                            return (
                              <>
                                {plainItems.map(({ item, idx }) => renderBlockItem(item, `p-${item.variantId}-${idx}`, 0))}
                                {sharedItems.map(({ item, idx }) => renderItemLine(item, `it-${item.variantId}-${idx}`))}
                                {groupScoped.map(d => {
                                  const couponItems = g.items.filter(x => couponsOn(x) === 1 && couponQualifiesItem(d, x.item));
                                  if (couponItems.length === 0) return null;
                                  const blockTotal = couponItems.reduce((s, { item }) => s + shareOfDiscount(d, item), 0);
                                  const badge = couponBadge(d);
                                  return (
                                    <div key={`blk-${locKey}-${d.code}`} style={{ marginBottom: '10px', padding: '8px 10px', background: '#fffdf7', borderRadius: '8px', border: '1px solid #fde68a', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', borderLeft: '3px solid #f59e0b' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}><span style={codeTruncStyle}>🎟️ {d.code}</span> <span style={{ fontSize: '8px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', padding: '1px 4px', borderRadius: '3px', flexShrink: 0 }}>{badge}</span></span>
                                        <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '12px', flexShrink: 0 }}>−{formatCurrency(blockTotal)}</span>
                                      </div>
                                      <div style={{ fontSize: '9px', color: (isBxgy(d) && d.bxgyHint) ? '#b45309' : '#a8896a', fontWeight: (isBxgy(d) && d.bxgyHint) ? 700 : 400, margin: '2px 0 6px' }}>{couponBlockNote(d)}</div>
                                      {isBxgy(d) && d.bxgyWarning && <div style={{ fontSize: '9px', color: '#b45309', margin: '-2px 0 6px' }}>⚠️ {d.bxgyWarning}</div>}
                                      {isBxgy(d) && d.bxgyNeedQty > 0 && <div style={{ margin: '-2px 0 6px' }}>{addYButton(d.code, d.bxgyNeedQty)}</div>}
                                      {couponItems.map(({ item, idx }) => renderBlockItem(item, `n-${item.variantId}-${idx}`, shareOfDiscount(d, item), itemTotalScopedDiscount(item), bxgyZeroNote(d, item, shareOfDiscount(d, item))))}
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </>
                );
              }
              // SINGLE-LOCATION: previous UI — one block per scoped coupon with ALL its eligible
              // items nested inside, plus the plain (non-coupon) items above.
              const scoped = (cart.discounts || []).filter(d => !isOrderLevel(d));
              const plain = (cart.items || []).map((it, idx) => ({ item: it, idx }))
                .filter(({ item }) => !scoped.some(d => couponQualifiesItem(d, item)));
              return (
                <>
                  {plain.map(({ item, idx }) => renderBlockItem(item, `p-${item.variantId}-${idx}`, 0))}
                  {scoped.map((d, di) => {
                    const its = (cart.items || []).map((it, idx) => ({ item: it, idx })).filter(({ item }) => couponQualifiesItem(d, item));
                    const eligSub = its.reduce((s, x) => s + effLineTotal(x.item), 0);
                    const badge = couponBadge(d);
                    return (
                      <div key={`db-${di}`} style={{ marginBottom: '10px', padding: '8px 10px', background: '#fffdf7', borderRadius: '8px', border: '1px solid #fde68a', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', borderLeft: '3px solid #f59e0b' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                            <span style={codeTruncStyle}>🎟️ {d.code}</span> <span style={{ fontSize: '8px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', padding: '1px 4px', borderRadius: '3px', flexShrink: 0 }}>{badge}</span>
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '12px' }}>−{formatCurrency(d.amount || 0)}</span>
                            {!discountsLocked && <button onClick={() => removeDiscount(d.code)} style={{ padding: '2px 6px', background: 'transparent', color: '#92400e', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: 0.6 }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}>✕</button>}
                          </span>
                        </div>
                        <div style={{ fontSize: '9px', color: (isBxgy(d) && d.bxgyHint) ? '#b45309' : '#a8896a', fontWeight: (isBxgy(d) && d.bxgyHint) ? 700 : 400, margin: '2px 0 6px' }}>
                          {couponBlockNote(d)}
                        </div>
                        {isBxgy(d) && d.bxgyWarning && <div style={{ fontSize: '9px', color: '#b45309', margin: '-2px 0 6px' }}>⚠️ {d.bxgyWarning}</div>}
                        {isBxgy(d) && d.bxgyNeedQty > 0 && <div style={{ margin: '-2px 0 6px' }}>{addYButton(d.code, d.bxgyNeedQty)}</div>}
                        {its.map(({ item, idx }) => {
                          const share = isBxgy(d) ? bxgyShare(d, item) : (eligSub > 0 ? (item.lineTotal / eligSub) * (d.amount || 0) : 0);
                          return renderBlockItem(item, `n-${item.variantId}-${idx}`, share, itemTotalScopedDiscount(item), bxgyZeroNote(d, item, share));
                        })}
                      </div>
                    );
                  })}
                </>
              );
            })()}

            <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '12px', paddingTop: '12px' }}>
              {(cart.discounts || []).length > 0 && (() => {
                // Total savings across all coupons (product/collection + order level)
                const totalSaved = cart.discounts.reduce((s, d) => s + (d.amount || 0), 0);
                return totalSaved > 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '700', color: '#16a34a', marginBottom: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '6px 10px' }}>
                    <span>💰 You Saved</span><span>−{formatCurrency(totalSaved)}</span>
                  </div>
                ) : null;
              })()}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                <span>Subtotal</span><span style={{ fontWeight: '600', color: '#374151' }}>{formatCurrency(cart.subtotal)}</span>
              </div>
              {(cart.discounts || []).some(d => isOrderLevel(d)) && (() => {
                // Only order-level discounts show here; product/collection coupons show at their line item.
                const orderDiscs = cart.discounts.filter(d => isOrderLevel(d));
                let running = cart.subtotal;
                const totalDiscount = orderDiscs.reduce((s, d) => s + (d.amount || 0), 0);
                return (
                  <>
                    {orderDiscs.map((d, i) => {
                      running -= (d.amount || 0);
                      return (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '12px', gap: '8px' }}>
                          <span style={{ color: '#475569', fontWeight: '600', display: 'flex', alignItems: 'baseline', gap: '4px', minWidth: 0 }}><span style={codeTruncStyle}>🎟️ {d.code}</span> <span style={{ fontWeight: '400', color: '#94a3b8', fontSize: '11px', flexShrink: 0 }}>(after: {formatCurrency(running)})</span></span>
                          <span style={{ color: '#16a34a', fontWeight: '700', flexShrink: 0 }}>-{formatCurrency(d.amount || 0)}</span>
                        </div>
                      );
                    })}
                    {orderDiscs.length > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '12px', fontWeight: '700', color: '#16a34a' }}>
                        <span>Total discount</span><span>-{formatCurrency(totalDiscount)}</span>
                      </div>
                    )}
                  </>
                );
              })()}
              {cart.roundOff !== undefined && cart.roundOff !== 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '12px', color: '#6b7280' }}>
                  <span>Round Off</span><span>{cart.roundOff > 0 ? '+' : ''}{formatCurrency(cart.roundOff)}</span>
                </div>
              )}
              <div style={{ background: '#1e293b', marginTop: '10px', padding: '12px 14px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '18px', fontWeight: '800', letterSpacing: '-0.3px' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cart.discounts && cart.discounts.length > 0 ? 'Grand Total' : 'Total'}</span><span style={{ color: '#f8fafc' }}>{formatCurrency(cart.total)}</span>
              </div>
            </div>

            {/* Discount Code — inside left column */}
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
              {(() => {
                // Which coupons are managed HERE: in multi-location there are no coupon blocks, so
                // list ALL applied coupons; in single-location scoped coupons live in their blocks,
                // so only list order-level here (avoids duplicate rows).
                const listDiscs = (cart.discounts || []).filter(d => isMultiLocation || isOrderLevel(d));
                if (listDiscs.length === 0) return null;
                return (
                <div style={{ marginBottom: '10px' }}>
                  {isMultiLocation && <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Applied Coupons</div>}
                  {listDiscs.map((d, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: d.source === 'pos_custom' ? '#fef9ee' : '#eef4ff', borderRadius: '8px', marginBottom: '4px', border: d.source === 'pos_custom' ? '1px solid #fde68a' : '1px solid #c7d7ff' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: d.source === 'pos_custom' ? '#92400e' : '#1e40af', display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, flex: 1 }}>
                        <span style={codeTruncStyle}>🎟️ {d.code}</span>
                        {!isOrderLevel(d)
                          ? <span style={{ fontSize: '9px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', padding: '1px 5px', borderRadius: '4px', flexShrink: 0 }}>{couponBadge(d)}</span>
                          : (d.source === 'pos_custom' ? <span style={{ fontSize: '9px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', padding: '1px 5px', borderRadius: '4px', flexShrink: 0 }}>POS</span> : '')}
                        <span style={{ color: '#16a34a', flexShrink: 0 }}>-{formatCurrency(d.amount || 0)}</span>
                        {isBxgy(d) && d.bxgyHint && <span title={d.bxgyHint} style={{ fontSize: '9px', color: '#b45309', ...codeTruncStyle }}>· {d.bxgyHint}</span>}
                        {isBxgy(d) && d.bxgyNeedQty > 0 && addYButton(d.code, d.bxgyNeedQty)}</span>
                      {!discountsLocked && <button onClick={() => removeDiscount(d.code)} style={{ padding: '2px 6px', background: 'transparent', color: d.source === 'pos_custom' ? '#92400e' : '#1e40af', border: 'none', fontSize: '11px', fontWeight: '600', cursor: 'pointer', opacity: 0.6, transition: 'opacity 0.15s', flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}>✕</button>}
                    </div>
                  ))}
                </div>
                );
              })()}
              {(!cart.discounts || cart.discounts.length < 2) && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (discountCode.trim() && !applyingDiscount) {
                        setApplyingDiscount(true);
                        applyDiscount(discountCode, localStorage.getItem('selectedLocation')).then(result => {
                          if (result.success) {
                            setDiscountCode('');
                            setDiscountToast({ type: 'success', message: result.autoAdded ? result.message : 'Discount applied!' });
                          } else {
                            setDiscountToast({ type: 'error', message: result.error || 'Invalid discount code' });
                          }
                          setApplyingDiscount(false);
                          setTimeout(() => setDiscountToast(null), 3000);
                        });
                      }
                    }
                  }}
                  placeholder="Coupon code"
                  disabled={discountsLocked}
                  style={{ flex: 1, padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', textTransform: 'uppercase', boxSizing: 'border-box', letterSpacing: '0.5px', fontWeight: '500', transition: 'border-color 0.15s, box-shadow 0.15s', opacity: discountsLocked ? 0.5 : 1 }}
                  onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  onClick={() => {
                    if (!discountCode.trim()) return;
                    setApplyingDiscount(true);
                    applyDiscount(discountCode, localStorage.getItem('selectedLocation')).then(result => {
                      if (result.success) {
                        setDiscountCode('');
                        setDiscountToast({ type: 'success', message: result.autoAdded ? result.message : 'Discount applied!' });
                      } else {
                        setDiscountToast({ type: 'error', message: result.error || 'Invalid discount code' });
                      }
                      setApplyingDiscount(false);
                      setTimeout(() => setDiscountToast(null), 3000);
                    });
                  }}
                  disabled={applyingDiscount || !discountCode.trim() || discountsLocked}
                  style={{
                    padding: '8px 16px', background: (applyingDiscount || !discountCode.trim() || discountsLocked) ? '#e2e8f0' : '#3b82f6',
                    color: (applyingDiscount || !discountCode.trim() || discountsLocked) ? '#94a3b8' : 'white',
                    border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700',
                    cursor: (applyingDiscount || !discountCode.trim() || discountsLocked) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s', boxShadow: (applyingDiscount || !discountCode.trim() || discountsLocked) ? 'none' : '0 2px 6px rgba(59,130,246,0.25)'
                  }}
                >
                  {applyingDiscount ? '...' : 'Apply'}
                </button>
              </div>
              )}
              {/* Coupon messages now render as a sticky banner at the top of this column. */}
              {/* Available Coupons — COLLAPSIBLE. The list is lazy-loaded only when expanded
                  (click the ▾ arrow), so opening checkout doesn't fetch coupons until wanted.
                  Applied coupons render elsewhere (per-line + order summary). */}
              {(!cart.discounts || cart.discounts.length < 2) && (
                <div style={{ marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setCouponsExpanded(v => !v)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'transparent', border: 'none', padding: '6px 4px', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: '10px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase' }}>Available Coupons</span>
                    <span style={{ width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', lineHeight: 1, color: '#6b7280', borderRadius: '6px', background: '#f3f4f6', transform: couponsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
                  </button>
                  {couponsExpanded && (
                    <div style={{ marginTop: '4px' }}>
                      {discountsLoading && visibleDiscounts.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 8px', background: '#fafaf9', border: '1px dashed #e7e5e4', borderRadius: '10px', color: '#9ca3af', fontSize: '11px' }}>
                          <span style={{ width: '14px', height: '14px', border: '2px solid #e5e7eb', borderTopColor: '#f59e0b', borderRadius: '50%', display: 'inline-block', animation: 'couponSpin 0.7s linear infinite' }} />
                          Loading available coupons…
                        </div>
                      ) : visibleDiscounts.filter(d => !cart.discounts?.some(cd => cd.code === d.code)).length === 0 ? (
                        <div style={{ padding: '8px', color: '#9ca3af', fontSize: '11px', textAlign: 'center', border: '1px dashed #e7e5e4', borderRadius: '10px' }}>No coupons available for this cart.</div>
                      ) : (
                        visibleDiscounts.filter(d => !cart.discounts?.some(cd => cd.code === d.code)).map((d, i) => {
                          // Eligibility is server-computed for ALL levels (order/product/collection/custom),
                          // so the list behaves consistently: enabled when eligible, greyed with a reason otherwise.
                          const isEligible = !!d.eligible;
                          const targetItem = (isOrderLevel(d) || isCustom(d)) ? null : (cart.items || []).find(it => couponMatchesItem(d, it));
                          return (
                          <div key={i} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '6px 8px', marginBottom: '4px',
                            background: isEligible ? '#fffbeb' : '#f3f4f6', border: `1px dashed ${isEligible ? '#f59e0b' : '#d1d5db'}`, borderRadius: '10px',
                            opacity: isEligible ? 1 : 0.55
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: isEligible ? '#92400e' : '#9ca3af', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                                <span style={codeTruncStyle}>{d.code}</span>
                                {!isOrderLevel(d) && <span style={{ fontSize: '8px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', padding: '1px 4px', borderRadius: '3px', flexShrink: 0 }}>{couponBadge(d)}</span>}
                              </div>
                              <div style={{ fontSize: '10px', color: '#78716c', marginTop: '1px' }}>
                                {d.description}
                                {/* Product/collection coupons: show only the offer, not the vague
                                    "add an eligible item" hint. Order + bundle keep their reason. */}
                                {!isEligible && d.reason && d.level !== 'products' && d.level !== 'collections' ? ` · ${d.reason}` : ''}
                                {isEligible && isBxgy(d) && d.hint ? ` · ${d.hint}` : ''}
                                {isEligible && isBxgy(d) && d.hint && d.bxgy && <div style={{ marginTop: '4px' }}>{addYButton(d.code, d.bxgy.y.qty)}</div>}
                                {targetItem ? ` · for ${targetItem.title.split(' / ')[0]}` : ''}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setApplyingDiscount(true);
                                applyDiscount(d.code, localStorage.getItem('selectedLocation')).then(result => {
                                  if (result.success) {
                                    setDiscountToast({ type: 'success', message: result.autoAdded ? result.message : `${d.code} applied!` });
                                  } else {
                                    setDiscountToast({ type: 'error', message: result.error || 'Could not apply' });
                                  }
                                  setApplyingDiscount(false);
                                  setTimeout(() => setDiscountToast(null), 3000);
                                });
                              }}
                              disabled={applyingDiscount || !isEligible || discountsLocked}
                              style={{
                                padding: '4px 10px', background: (isEligible && !discountsLocked) ? '#f59e0b' : '#d1d5db', color: 'white',
                                border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                                cursor: (applyingDiscount || !isEligible || discountsLocked) ? 'not-allowed' : 'pointer', flexShrink: 0, marginLeft: '8px'
                              }}
                            >
                              Apply
                            </button>
                          </div>
                        );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Checkout Options */}
          <div className="checkout-scrollbar" style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>

            {/* Fulfillment + Customer row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              {/* Fulfillment Source */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '8px', color: '#475569', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Fulfill From
                </label>
                {isMultiLocation ? (
                  <div style={{ fontSize: '12px', color: '#374151' }}>
                    <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600', marginBottom: '4px' }}>Multi-location — auto per item</div>
                    {(() => {
                      const locs = {};
                      cart.items.forEach(item => {
                        const locId = item.locationId || locationId;
                        if (!locs[locId]) locs[locId] = { name: item.locationName || '', type: item.locationType || 'store', count: 0 };
                        locs[locId].count += item.quantity;
                      });
                      return Object.values(locs).map((loc, i) => (
                        <div key={i} style={{ fontSize: '11px', color: '#7a6b5d', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: loc.type === 'warehouse' ? '#f59e0b' : '#22c55e', display: 'inline-block' }}></span>
                          {loc.name} ({loc.count} {loc.count === 1 ? 'item' : 'items'}) — {loc.type === 'warehouse' ? '📦 dispatch (address required)' : 'in-store pickup'}
                        </div>
                      ));
                    })()}
                  </div>
                ) : (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '10px', background: fulfillmentSource === 'warehouse' ? '#fffbeb' : '#f0fdf4', border: `1.5px solid ${fulfillmentSource === 'warehouse' ? '#fde68a' : '#bbf7d0'}` }}>
                  <span style={{ fontSize: '18px', lineHeight: 1 }}>{fulfillmentSource === 'warehouse' ? '📦' : '🏪'}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: fulfillmentSource === 'warehouse' ? '#92400e' : '#166534', lineHeight: 1.2 }}>
                      {fulfillmentSource === 'warehouse' ? 'Warehouse' : 'Store'}
                    </span>
                    {effectiveLocationName && (
                      <span style={{ fontSize: '11px', color: '#78716c', lineHeight: 1.2 }}>📍 {effectiveLocationName}</span>
                    )}
                    {hasWarehouseItems && (
                      <span style={{ fontSize: '10px', color: '#d97706', fontWeight: '600', lineHeight: 1.3, marginTop: '1px' }}>Dispatch — address required</span>
                    )}
                    {hasCustomizedItem && !hasWarehouseItems && (
                      <span style={{ fontSize: '10px', color: '#c88346', lineHeight: 1.3, marginTop: '1px' }}>Custom items — warehouse required</span>
                    )}
                  </div>
                </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '8px', color: '#475569', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Payment
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => { cancelEzetapPayment(); setPaymentMethod(e.target.value); }}
                  style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', fontWeight: '600', color: paymentMethod ? '#1e293b' : '#94a3b8', backgroundColor: paymentInProgress ? '#f1f5f9' : 'white', transition: 'border-color 0.15s, box-shadow 0.15s', cursor: paymentInProgress ? 'not-allowed' : 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' stroke='%23475569' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}
                  disabled={paymentInProgress}
                >
                  <option value="" disabled>— Select Payment Method —</option>
                  <option value="cash">Cash</option>
                  <option value="card" disabled={!hasEzetap}>Card{!hasEzetap ? ' (terminal not configured)' : ''}</option>
                  <option value="upi" disabled={!hasEzetap}>UPI{!hasEzetap ? ' (terminal not configured)' : ''}</option>
                  <option value="netbanking">NetBanking / NEFT / RTGS / Bank Transfer</option>
                  <option value="split" disabled={!hasEzetap}>Cash + UPI / Card{!hasEzetap ? ' (terminal not configured)' : ''}</option>
                  <option value="splitUpiCard" disabled={!hasEzetap}>UPI + Card (Split){!hasEzetap ? ' (terminal not configured)' : ''}</option>
                  <option value="district">District by Zomato</option>
                  <option value="snapmint" disabled={!hasSnapmint}>Snapmint{!hasSnapmint ? ' (not configured)' : ''}</option>
                </select>
                {/* Bug 2: once a method is selected the coupon section locks. This lets the staff
                    go back to edit coupons/cart without losing customer/staff details. */}
                {paymentMethod && !paymentInProgress && (
                  <button
                    type="button"
                    onClick={() => { cancelEzetapPayment(); resetPaymentPhase(); setCouponsExpanded(true); }}
                    style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'transparent', border: '1px solid #cbd5e1', color: '#475569', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    ← Back to coupons / edit cart
                  </button>
                )}
              </div>
            </div>

            {/* Staff Name */}
            {posStaffs.length > 0 && (
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                  Staff Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={selectedStaff}
                  onChange={(e) => setSelectedStaff(e.target.value)}
                  disabled={paymentInProgress}
                  style={{ flex: 1, padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', fontWeight: '600', color: selectedStaff ? '#1e293b' : '#94a3b8', backgroundColor: paymentInProgress ? '#f1f5f9' : 'white', cursor: paymentInProgress ? 'not-allowed' : 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' stroke='%23475569' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}
                >
                  <option value="" disabled>— Select Staff —</option>
                  {posStaffs.map(s => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Customer */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '8px', color: '#475569', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Customer {fulfillmentSource === 'warehouse' && <span style={{ color: '#dc2626' }}>*</span>}
              </label>

              {selectedCustomer ? (
                <>
                <div style={{ padding: '10px 12px', background: '#f1f5f9', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1.5px solid #cbd5e1' }}>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>✓ {selectedCustomer.firstName} {selectedCustomer.lastName}</span>
                    <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>{selectedCustomer.email || selectedCustomer.phone}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={handleEditCustomerInfo} disabled={paymentInProgress} style={{ padding: '4px 12px', background: paymentInProgress ? '#f1f5f9' : '#f8fafc', color: paymentInProgress ? '#94a3b8' : '#475569', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '11px', fontWeight: '600', cursor: paymentInProgress ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}>Edit</button>
                    <button onClick={() => { setSelectedCustomer(null); setEditingCustomerInfo(false); setCustomerEditError(''); }} disabled={paymentInProgress} style={{ padding: '4px 12px', background: paymentInProgress ? '#f1f5f9' : 'white', color: paymentInProgress ? '#94a3b8' : '#475569', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '11px', fontWeight: '600', cursor: paymentInProgress ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}>Change</button>
                  </div>
                </div>
                {/* Warning for missing customer info */}
                {getCustomerMissingFields(selectedCustomer).length > 0 && !editingCustomerInfo && (
                  <div style={{ marginTop: '6px', padding: '8px 12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#92400e', fontWeight: '600' }}>
                      ⚠️ Missing: {getCustomerMissingFields(selectedCustomer).join(', ')}
                    </span>
                    <button onClick={handleEditCustomerInfo} style={{ padding: '3px 10px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Edit</button>
                  </div>
                )}
                {/* Inline edit form for customer info */}
                {editingCustomerInfo && (
                  <div style={{ marginTop: '6px', padding: '10px 12px', background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                      <input type="text" placeholder="First Name *" value={customerEditData.firstName} onChange={(e) => setCustomerEditData({...customerEditData, firstName: e.target.value})}
                        className="checkout-input" style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                      <input type="text" placeholder="Last Name *" value={customerEditData.lastName} onChange={(e) => setCustomerEditData({...customerEditData, lastName: e.target.value})}
                        className="checkout-input" style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                      <input type="email" placeholder="Email" value={customerEditData.email} onChange={(e) => setCustomerEditData({...customerEditData, email: e.target.value})}
                        className="checkout-input" style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                      <input type="tel" placeholder="Phone" value={customerEditData.phone} onChange={(e) => setCustomerEditData({...customerEditData, phone: e.target.value})}
                        className="checkout-input" style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={handleSaveCustomerInfo} disabled={savingCustomer} style={{ flex: 1, padding: '7px', background: savingCustomer ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: savingCustomer ? 'not-allowed' : 'pointer' }}>
                        {savingCustomer ? 'Saving...' : 'Save & Update'}
                      </button>
                      <button onClick={() => { setEditingCustomerInfo(false); setCustomerEditError(''); }} style={{ padding: '7px 14px', background: 'white', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                    </div>
                    {customerEditError && (
                      <div style={{ marginTop: '6px', padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '11px', color: '#dc2626', fontWeight: '600' }}>
                        ⚠️ {customerEditError}
                      </div>
                    )}
                  </div>
                )}
                </>
              ) : showNewCustomerForm ? (
                <div style={{ padding: '12px', background: '#fafafa', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input type="text" placeholder="First Name *" value={newCustomer.firstName} onChange={(e) => setNewCustomer({...newCustomer, firstName: e.target.value})}
                      className="checkout-input" style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', transition: 'border-color 0.15s, box-shadow 0.15s' }} />
                    <input type="text" placeholder="Last Name *" value={newCustomer.lastName} onChange={(e) => setNewCustomer({...newCustomer, lastName: e.target.value})}
                      className="checkout-input" style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', transition: 'border-color 0.15s, box-shadow 0.15s' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                    <input type="email" placeholder="Email *" value={newCustomer.email} onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                      className="checkout-input" style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', transition: 'border-color 0.15s, box-shadow 0.15s' }} />
                    <input type="tel" placeholder="Phone *" value={newCustomer.phone} onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                      className="checkout-input" style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', transition: 'border-color 0.15s, box-shadow 0.15s' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handleCreateCustomer} className="checkout-btn-primary" style={{ flex: 1, padding: '9px', background: '#334155', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>Create</button>
                    <button onClick={() => { setShowNewCustomerForm(false); setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' }); }}
                      style={{ padding: '9px 16px', background: 'white', color: '#374151', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.15s' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <input type="text" value={customerSearch} onChange={handleSearchChange} placeholder="Search by name, email, or phone..."
                    className="checkout-input" style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s' }} />
                  {searchResults.length > 0 && (
                    <div className="checkout-scrollbar" style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '10px', marginTop: '4px', maxHeight: '160px', overflow: 'auto', zIndex: 10, boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)' }}>
                      {searchResults.map((customer) => (
                        <div key={customer.id} onClick={() => {
                          selectCustomer(customer);
                        }}
                          style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{customer.firstName} {customer.lastName}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{customer.phone || <span style={{ color: '#dc2626' }}>No mobile number</span>}{customer.email ? ` • ${customer.email}` : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => setShowNewCustomerForm(true)}
                    style={{ width: '100%', marginTop: '8px', padding: '8px', background: 'white', color: '#64748b', border: '1.5px dashed #cbd5e1', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'white'; }}>
                    + New Customer
                  </button>
                </div>
              )}
            </div>

            {/* Shipping Address — Always required */}
            {(fulfillmentSource === 'warehouse' || fulfillmentSource === 'store' || gstBilling) && (
              <div style={{ marginBottom: '16px', padding: '14px', background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderRadius: '16px', border: '1.5px solid #fde68a' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '10px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  📦 {fulfillmentSource === 'warehouse' ? 'Shipping Address' : 'Customer Address'} <span style={{ color: '#dc2626' }}>*</span>
                </label>

                {!selectedCustomer ? (
                  <div style={{ fontSize: '12px', color: '#92400e' }}>⚠️ Select a customer first</div>
                ) : (
                  <>
                    {customerAddresses.length > 0 && !showNewAddressForm && (
                      <div style={{ marginBottom: '8px' }}>
                        {customerAddresses.map((address) => (
                          <label key={address.id} style={{
                            display: 'block', padding: '8px', marginBottom: '4px',
                            background: selectedAddressId === address.id ? '#dbeafe' : 'white',
                            border: selectedAddressId === address.id ? '2px solid #2563eb' : '1px solid #d1d5db',
                            borderRadius: '10px', cursor: paymentInProgress ? 'not-allowed' : 'pointer', fontSize: '12px',
                            opacity: paymentInProgress ? 0.6 : 1, pointerEvents: paymentInProgress ? 'none' : 'auto'
                          }} onClick={() => handleSelectAddress(address)}>
                            <div style={{ display: 'flex', alignItems: 'start', gap: '6px' }}>
                              <input type="radio" name="address" checked={selectedAddressId === address.id} onChange={() => handleSelectAddress(address)} style={{ marginTop: '1px', cursor: 'pointer' }} />
                              <div>
                                <div style={{ fontWeight: '600', color: '#111827' }}>{address.address1}{address.address2 && `, ${address.address2}`}</div>
                                <div style={{ color: '#6b7280' }}>{address.city}, {address.province} {address.zip}</div>
                                {address.default && <span style={{ fontSize: '10px', padding: '1px 5px', background: '#dcfce7', color: '#166534', borderRadius: '3px', fontWeight: '600' }}>DEFAULT</span>}
                              </div>
                            </div>
                          </label>
                        ))}
                        <button onClick={() => { setShowNewAddressForm(true); setSelectedAddressId(null); setShippingAddress({ address1: '', address2: '', city: '', province: '', zip: '', country: 'India' }); setPincodeVerified(false); setPincodeError(''); setPincodeResults(null); }}
                          disabled={paymentInProgress}
                          style={{ width: '100%', padding: '5px', marginTop: '4px', background: paymentInProgress ? '#f1f5f9' : 'white', color: paymentInProgress ? '#9ca3af' : '#92400e', border: `1px solid ${paymentInProgress ? '#d1d5db' : '#fbbf24'}`, borderRadius: '5px', fontSize: '11px', fontWeight: '600', cursor: paymentInProgress ? 'not-allowed' : 'pointer' }}>
                          + Add New Address
                        </button>
                      </div>
                    )}

                    {(showNewAddressForm || customerAddresses.length === 0) && (
                      <div>
                        {customerAddresses.length > 0 && (
                          <button onClick={() => { setShowNewAddressForm(false); if (customerAddresses.length > 0) handleSelectAddress(customerAddresses[0]); }}
                            style={{ padding: '4px 10px', marginBottom: '8px', background: 'white', color: '#92400e', border: '1px solid #fbbf24', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>
                            ← Saved Addresses
                          </button>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                          <input type="text" placeholder="Address Line 1 *" value={shippingAddress.address1} onChange={(e) => setShippingAddress({...shippingAddress, address1: e.target.value})}
                            style={{ gridColumn: '1 / -1', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px' }} />
                          <input type="text" placeholder="Address Line 2" value={shippingAddress.address2} onChange={(e) => setShippingAddress({...shippingAddress, address2: e.target.value})}
                            style={{ gridColumn: '1 / -1', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px' }} />
                          <input type="text" placeholder="Pincode *" value={shippingAddress.zip} onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setShippingAddress({...shippingAddress, zip: val});
                            // Auto-populate city & state from pincode via backend proxy
                            if (val.length === 6) {
                              setPincodeLookupLoading(true);
                              api.get(`/checkout/pincode-lookup/${val}`)
                                .then(resp => {
                                  if (resp.data?.success) {
                                    setShippingAddress(prev => ({
                                      ...prev,
                                      zip: val,
                                      city: resp.data.city || '',
                                      province: resp.data.state || ''
                                    }));
                                  }
                                })
                                .catch(() => {})
                                .finally(() => setPincodeLookupLoading(false));
                            }
                          }}
                            style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px' }} />
                          <div style={{ position: 'relative' }}>
                            <input type="text" placeholder={pincodeLookupLoading ? 'Loading...' : 'City *'} value={shippingAddress.city} onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px', boxSizing: 'border-box', background: pincodeLookupLoading ? '#f3f4f6' : 'white' }} />
                            {pincodeLookupLoading && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px' }}>⏳</span>}
                          </div>
                          <div style={{ position: 'relative' }}>
                            <input type="text" placeholder={pincodeLookupLoading ? 'Loading...' : 'State *'} value={shippingAddress.province} onChange={(e) => setShippingAddress({...shippingAddress, province: e.target.value})}
                              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px', boxSizing: 'border-box', background: pincodeLookupLoading ? '#f3f4f6' : 'white' }} />
                            {pincodeLookupLoading && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px' }}>⏳</span>}
                          </div>
                          <input type="text" placeholder="Country" value={shippingAddress.country} onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                            style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px' }} />
                        </div>
                      </div>
                    )}

                    {/* Pincode Verification — only for warehouse fulfillment */}
                    {fulfillmentSource === 'warehouse' && shippingAddress.zip && shippingAddress.zip.trim().length >= 5 && (
                      <div style={{ marginTop: '8px' }}>
                        {!pincodeVerified ? (
                          <button onClick={verifyPincode} disabled={pincodeChecking}
                            style={{ width: '100%', padding: '7px', background: pincodeChecking ? '#e5e7eb' : '#2563eb', color: pincodeChecking ? '#6b7280' : 'white', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: '600', cursor: pincodeChecking ? 'not-allowed' : 'pointer' }}>
                            {pincodeChecking ? 'Verifying...' : `Verify Pincode ${shippingAddress.zip.trim()}`}
                          </button>
                        ) : (
                          <div style={{ padding: '6px 10px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '10px', fontSize: '12px', fontWeight: '600', color: '#166534' }}>
                            ✓ Pincode verified — delivery available
                          </div>
                        )}
                        {pincodeError && (
                          <div style={{ marginTop: '4px', padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', fontSize: '12px', fontWeight: '600', color: '#dc2626' }}>✗ {pincodeError}</div>
                        )}
                        {pincodeResults && (
                          <div style={{ marginTop: '4px' }}>
                            {pincodeResults.map((result, idx) => (
                              <div key={idx} style={{ padding: '5px 8px', marginBottom: '2px', background: result.deliverable ? '#f0fdf4' : '#fef2f2', border: `1px solid ${result.deliverable ? '#bbf7d0' : '#fecaca'}`, borderRadius: '5px', fontSize: '11px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' }}>
                                  <span style={{ fontWeight: '600', color: '#374151', flex: '1', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{result.title}</span>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                    {result.isMto ? <span style={{ color: '#fff', background: '#7c3aed', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px' }}>MTO</span> : result.isCustomSize ? <span style={{ color: '#fff', background: '#c88346', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px' }}>Custom</span> : null}
                                    <span style={{ fontWeight: '600', color: result.deliverable ? '#166534' : '#dc2626' }}>{result.deliverable ? '✓' : '✗'}</span>
                                    {result.deliverable && result.deliveryDate && (() => { const d = new Date(result.deliveryDate); const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return <span style={{ fontWeight: '600', color: '#166534' }}>{d.getDate()} {m[d.getMonth()]}</span>; })()}
                                  </span>
                                </div>
                                <div style={{ color: '#6b7280', marginTop: '1px' }}>
                                  SKU: {result.sku}
                                  {result.isMto && result.isMtoMsg && <span style={{ color: '#7c3aed', fontStyle: 'italic' }}> • {result.isMtoMsg}</span>}
                                  {!result.deliverable && result.error && ` • ${result.error}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Email Receipt */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: selectedCustomer?.email ? 'pointer' : 'not-allowed', gap: '6px', opacity: selectedCustomer?.email ? 1 : 0.5 }}>
                <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} disabled={!selectedCustomer?.email}
                  style={{ width: '14px', height: '14px', cursor: selectedCustomer?.email ? 'pointer' : 'not-allowed' }} />
                <span style={{ fontSize: '12px', color: '#374151' }}>
                  {selectedCustomer?.email ? <>Email receipt to <strong>{selectedCustomer.email}</strong></> : 'Email receipt (select customer with email)'}
                </span>
              </label>
            </div>

            {/* GST Billing */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: paymentInProgress ? 'not-allowed' : 'pointer', gap: '6px', opacity: paymentInProgress ? 0.5 : 1 }}>
                <input type="checkbox" checked={gstBilling} onChange={(e) => { setGstBilling(e.target.checked); if (!e.target.checked) { setGstin(''); setGstCompanyName(''); setDiffBilling(false); setBillingAddress({ address1: '', address2: '', city: '', province: '', zip: '', country: 'India' }); setSelectedBillingAddressId(null); setBillingAddressSource('new'); } }}
                  disabled={paymentInProgress} style={{ width: '14px', height: '14px', cursor: paymentInProgress ? 'not-allowed' : 'pointer' }} />
                <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>GST Billing</span>
              </label>
              {gstBilling && (
                <div style={{ marginTop: '6px' }}>
                  <input
                    type="text"
                    value={gstin}
                    disabled={paymentInProgress}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
                      setGstin(val);
                      if (val.length === 15 && isValidGST(val)) {
                        setGstLookupLoading(true);
                        api.get(`/checkout/gst-lookup/${val}`).then(res => {
                          if (res.data.success && res.data.legalName) {
                            setGstCompanyName(res.data.legalName);
                          }
                        }).catch(() => {}).finally(() => setGstLookupLoading(false));
                      }
                    }}
                    placeholder="Enter 15-digit GSTIN"
                    maxLength={15}
                    style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px', fontFamily: 'monospace', letterSpacing: '1px', boxSizing: 'border-box', textTransform: 'uppercase', opacity: paymentInProgress ? 0.5 : 1, cursor: paymentInProgress ? 'not-allowed' : 'text' }}
                  />
                  {gstin && gstin.length < 15 && (
                    <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '3px' }}>GSTIN must be 15 characters ({gstin.length}/15)</div>
                  )}
                  {gstin && gstin.length === 15 && !isValidGST(gstin) && (
                    <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '3px' }}>✗ Invalid GSTIN format</div>
                  )}
                  {gstin && gstin.length === 15 && isValidGST(gstin) && (
                    <div style={{ fontSize: '11px', color: '#166534', marginTop: '3px' }}>✓ Valid GSTIN</div>
                  )}
                  <div style={{ position: 'relative', marginTop: '6px' }}>
                    <input
                      type="text"
                      value={gstCompanyName}
                      onChange={(e) => setGstCompanyName(e.target.value)}
                      placeholder={gstLookupLoading ? 'Fetching company name...' : 'Company Name *'}
                      disabled={gstLookupLoading || paymentInProgress}
                      style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px', boxSizing: 'border-box', background: (gstLookupLoading || paymentInProgress) ? '#f9fafb' : 'white', opacity: paymentInProgress ? 0.5 : 1, cursor: paymentInProgress ? 'not-allowed' : 'text' }}
                    />
                    {gstLookupLoading && <span style={{ position: 'absolute', right: '10px', top: '8px', fontSize: '12px' }}>⏳</span>}
                  </div>

                  {/* Different Billing Address — inside GST */}
                  <div style={{ marginTop: '8px' }}>
                    {!diffBilling && (
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', fontStyle: 'italic' }}>
                        📋 Shipping address will be used as billing address
                      </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', cursor: paymentInProgress ? 'not-allowed' : 'pointer', gap: '6px', opacity: paymentInProgress ? 0.5 : 1 }}>
                      <input type="checkbox" checked={diffBilling} onChange={(e) => { setDiffBilling(e.target.checked); if (!e.target.checked) { setBillingAddress({ address1: '', address2: '', city: '', province: '', zip: '', country: 'India' }); setSelectedBillingAddressId(null); setBillingAddressSource('new'); } }}
                        disabled={paymentInProgress} style={{ width: '14px', height: '14px', cursor: paymentInProgress ? 'not-allowed' : 'pointer' }} />
                      <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>Different Billing Address</span>
                    </label>
                    {diffBilling && (
                      <div style={{ marginTop: '6px', padding: '10px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', marginBottom: '6px', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>🧾 Billing Address</label>

                        {/* Toggle: Existing vs New */}
                        {selectedCustomer && customerAddresses.length > 0 && (
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                            <button onClick={() => { setBillingAddressSource('existing'); setBillingAddress({ address1: '', address2: '', city: '', province: '', zip: '', country: 'India' }); }}
                              style={{ flex: 1, padding: '5px', fontSize: '11px', fontWeight: '600', border: billingAddressSource === 'existing' ? '2px solid #2563eb' : '1px solid #d1d5db', borderRadius: '10px', background: billingAddressSource === 'existing' ? '#dbeafe' : 'white', cursor: 'pointer', color: billingAddressSource === 'existing' ? '#1e40af' : '#6b7280' }}>
                              Saved Addresses
                            </button>
                            <button onClick={() => { setBillingAddressSource('new'); setSelectedBillingAddressId(null); setBillingAddress({ address1: '', address2: '', city: '', province: '', zip: '', country: 'India' }); }}
                              style={{ flex: 1, padding: '5px', fontSize: '11px', fontWeight: '600', border: billingAddressSource === 'new' ? '2px solid #2563eb' : '1px solid #d1d5db', borderRadius: '10px', background: billingAddressSource === 'new' ? '#dbeafe' : 'white', cursor: 'pointer', color: billingAddressSource === 'new' ? '#1e40af' : '#6b7280' }}>
                              New Address
                            </button>
                          </div>
                        )}

                        {/* Existing address list */}
                        {billingAddressSource === 'existing' && customerAddresses.length > 0 && (
                          <div>
                            {customerAddresses.map((addr) => (
                              <label key={addr.id} style={{
                                display: 'block', padding: '8px', marginBottom: '4px',
                                background: selectedBillingAddressId === addr.id ? '#dbeafe' : 'white',
                                border: selectedBillingAddressId === addr.id ? '2px solid #2563eb' : '1px solid #d1d5db',
                                borderRadius: '10px', cursor: 'pointer', fontSize: '12px'
                              }} onClick={() => { setSelectedBillingAddressId(addr.id); setBillingAddress({ address1: addr.address1 || '', address2: addr.address2 || '', city: addr.city || '', province: addr.province || '', zip: addr.zip || '', country: addr.country || 'India' }); }}>
                                <div style={{ display: 'flex', alignItems: 'start', gap: '6px' }}>
                                  <input type="radio" name="billingAddr" checked={selectedBillingAddressId === addr.id} readOnly style={{ marginTop: '1px', cursor: 'pointer' }} />
                                  <div>
                                    <div style={{ fontWeight: '600', color: '#111827' }}>{addr.address1}{addr.address2 && `, ${addr.address2}`}</div>
                                    <div style={{ color: '#6b7280' }}>{addr.city}, {addr.province} {addr.zip}</div>
                                    {addr.default && <span style={{ fontSize: '10px', padding: '1px 5px', background: '#dcfce7', color: '#166534', borderRadius: '3px', fontWeight: '600' }}>DEFAULT</span>}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}

                        {/* New billing address form */}
                        {(billingAddressSource === 'new' || !selectedCustomer || customerAddresses.length === 0) && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                            <input type="text" placeholder="Address Line 1 *" value={billingAddress.address1} onChange={(e) => setBillingAddress({...billingAddress, address1: e.target.value})}
                              style={{ gridColumn: '1 / -1', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px' }} />
                            <input type="text" placeholder="Address Line 2" value={billingAddress.address2} onChange={(e) => setBillingAddress({...billingAddress, address2: e.target.value})}
                              style={{ gridColumn: '1 / -1', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px' }} />
                            <input type="text" placeholder="Pincode *" value={billingAddress.zip} onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setBillingAddress({...billingAddress, zip: val});
                              if (val.length === 6) {
                                setBillingPincodeLookupLoading(true);
                                api.get(`/checkout/pincode-lookup/${val}`)
                                  .then(resp => {
                                    if (resp.data?.success) {
                                      setBillingAddress(prev => ({ ...prev, zip: val, city: resp.data.city || '', province: resp.data.state || '' }));
                                    }
                                  }).catch(() => {})
                                  .finally(() => setBillingPincodeLookupLoading(false));
                              }
                            }}
                              style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px' }} />
                            <div style={{ position: 'relative' }}>
                              <input type="text" placeholder={billingPincodeLookupLoading ? 'Loading...' : 'City *'} value={billingAddress.city} onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px', boxSizing: 'border-box', background: billingPincodeLookupLoading ? '#f3f4f6' : 'white' }} />
                              {billingPincodeLookupLoading && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px' }}>⏳</span>}
                            </div>
                            <div style={{ position: 'relative' }}>
                              <input type="text" placeholder={billingPincodeLookupLoading ? 'Loading...' : 'State *'} value={billingAddress.province} onChange={(e) => setBillingAddress({...billingAddress, province: e.target.value})}
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px', boxSizing: 'border-box', background: billingPincodeLookupLoading ? '#f3f4f6' : 'white' }} />
                              {billingPincodeLookupLoading && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px' }}>⏳</span>}
                            </div>
                            <input type="text" placeholder="Country" value={billingAddress.country} onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value})}
                              style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '13px' }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '12px' }}>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Order notes (optional)" rows="2"
                className="checkout-input" style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s' }} />
            </div>

            {/* Split payment details + Ezetap 2-step terminal */}
            {paymentMethod === 'split' && (
              <div style={{ marginBottom: '16px', padding: '14px', background: '#fff7ed', borderRadius: '10px', border: '2px solid #f97316' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#c2410c', marginBottom: '10px' }}>
                  Split — Total: {formatCurrency(cart.total)}
                </div>

                {/* Amount inputs — only editable before step 1 starts */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                  <div>
                    <label style={{ fontSize: '13px', color: '#15803d', fontWeight: '700', display: 'block', marginBottom: '5px' }}>💵 Cash Amount</label>
                    <input type="number" placeholder="0.00" value={splitCash}
                      onChange={(e) => { let val = e.target.value; const num = parseFloat(val); if (num > parseFloat(cart.total)) val = parseFloat(cart.total).toFixed(2); setSplitCash(val); if (val) { setSplitCard((parseFloat(cart.total) - parseFloat(val) > 0 ? (parseFloat(cart.total) - parseFloat(val)).toFixed(2) : '0.00')); } else { setSplitCard(''); } }}
                      disabled={splitStep > 0}
                      min="0" max={cart.total} step="0.01"
                      style={{ width: '100%', padding: '10px 12px', border: '2px solid #16a34a', borderRadius: '10px', fontSize: '16px', fontWeight: '700', boxSizing: 'border-box', background: splitStep > 0 ? '#f3f4f6' : 'white' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: '#7c3aed', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '5px' }}>
                      {splitCardMode === 'UPI' ? '📱' : '💳'}
                      <select value={splitCardMode} onChange={(e) => setSplitCardMode(e.target.value)}
                        disabled={splitStep > 0}
                        style={{ fontSize: '13px', border: '1.5px solid #7c3aed', background: '#f5f3ff', borderRadius: '6px', padding: '2px 6px', fontWeight: '700', color: '#7c3aed', cursor: splitStep > 0 ? 'default' : 'pointer', appearance: 'auto' }}>
                        <option value="CARD">Card Amount</option>
                        <option value="UPI">UPI Amount</option>
                      </select>
                    </label>
                    <input type="number" placeholder="0.00" value={splitCard}
                      onChange={(e) => { let val = e.target.value; const num = parseFloat(val); if (num > parseFloat(cart.total)) val = parseFloat(cart.total).toFixed(2); setSplitCard(val); if (val) { setSplitCash((parseFloat(cart.total) - parseFloat(val) > 0 ? (parseFloat(cart.total) - parseFloat(val)).toFixed(2) : '0.00')); } else { setSplitCash(''); } }}
                      disabled={splitStep > 0}
                      min="0" max={cart.total} step="0.01"
                      style={{ width: '100%', padding: '10px 12px', border: '2px solid #7c3aed', borderRadius: '10px', fontSize: '16px', fontWeight: '700', boxSizing: 'border-box', background: splitStep > 0 ? '#f3f4f6' : 'white' }}
                    />
                  </div>
                </div>

                {/* Amount validation */}
                {splitCash && splitCard && splitStep === 0 && (
                  <div style={{ marginBottom: '8px', fontSize: '11px', fontWeight: '600',
                    color: Math.abs((parseFloat(splitCash) || 0) + (parseFloat(splitCard) || 0) - parseFloat(cart.total)) < 0.01 ? '#27ae60' : '#c0392b'
                  }}>
                    {Math.abs((parseFloat(splitCash) || 0) + (parseFloat(splitCard) || 0) - parseFloat(cart.total)) < 0.01
                      ? '✓ Amounts match' : `✗ Diff: ${formatCurrency(Math.abs((parseFloat(splitCash) || 0) + (parseFloat(splitCard) || 0) - parseFloat(cart.total)))}`}
                  </div>
                )}

                {/* Step indicator */}
                {splitStep > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', fontSize: '11px', color: '#6b7280' }}>
                    <span style={{ width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: 'white', background: splitStep >= 2 ? '#22c55e' : '#2563eb' }}>{splitStep >= 2 ? '✓' : '1'}</span>
                    <span style={{ color: splitStep >= 2 ? '#22c55e' : '#2563eb', fontWeight: '600' }}>Cash {formatCurrency(splitCash)}</span>
                    <span style={{ flex: 1, height: '1px', background: splitStep >= 2 ? '#22c55e' : '#d1d5db' }} />
                    <span style={{ width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: 'white', background: splitStep === 3 ? '#22c55e' : splitStep === 2 ? '#2563eb' : '#d1d5db' }}>{splitStep === 3 ? '✓' : '2'}</span>
                    <span style={{ color: splitStep === 3 ? '#22c55e' : splitStep === 2 ? '#2563eb' : '#9ca3af', fontWeight: '600' }}>{splitCardMode} {formatCurrency(splitCard)}</span>
                  </div>
                )}

                {/* Pincode verification needed message */}
                {splitStep === 0 && splitCash && splitCard && Math.abs((parseFloat(splitCash) || 0) + (parseFloat(splitCard) || 0) - parseFloat(cart.total)) < 0.01 && hasWarehouseItems && !pincodeVerified && (
                  <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', marginTop: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                      💵💳 Verify delivery pincode above to enable split payment
                    </div>
                  </div>
                )}

                {/* Step 0: Confirm cash received (physical — no terminal needed), then proceed to Card/UPI terminal */}
                {splitStep === 0 && splitCash && splitCard && Math.abs((parseFloat(splitCash) || 0) + (parseFloat(splitCard) || 0) - parseFloat(cart.total)) < 0.01 && (!hasWarehouseItems || pincodeVerified) && (
                  <>
                  <button onClick={() => {
                    if (!selectedCustomer) { alert('Please select a customer before proceeding'); return; }
                    if (!validatePrePayment()) return;
                    // Cash is physical — skip terminal, record as confirmed and move to step 2
                    setSplitEzetapDetails([{ txnId: '', p2pRequestId: '', receiptUrl: '', paymentMode: 'CASH', externalRefNumber: `POS-SPLIT-CASH-${Date.now()}`, amount: parseFloat(splitCash), raw: {} }]);
                    setSplitStep(2);
                  }}
                    disabled={!selectedCustomer}
                    style={{ width: '100%', padding: '10px', background: !selectedCustomer ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: !selectedCustomer ? 'not-allowed' : 'pointer' }}>
                    Step 1 — Confirm Cash {formatCurrency(splitCash)} Received
                  </button>
                  {!selectedCustomer && (
                    <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '6px', textAlign: 'center' }}>⚠️ Select a customer above to proceed</div>
                  )}
                  </>
                )}

                {/* Step 2: show terminal waiting UI for Card/UPI leg */}
                {(splitStep === 2 && ezetapProcessing) && (ezetapStatus === 'INITIATED' || ezetapStatus === 'POLLING') && (() => {
                  const currentMode = splitCardMode;
                  const currentAmt = splitCard;
                  const isUpi = currentMode === 'UPI';
                  const totalSec = isUpi ? EZETAP_TIMEOUT_SECONDS.UPI : EZETAP_TIMEOUT_SECONDS.CARD;
                  const progress = totalSec > 0 ? ezetapCountdown / totalSec : 0;
                  const radius = 34;
                  const circ = 2 * Math.PI * radius;
                  const dashOffset = circ * (1 - progress);
                  const isUrgent = ezetapCountdown <= 30;
                  const timerColor = isUrgent ? '#ef4444' : '#2563eb';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '4px 0' }}>
                      <div style={{ position: 'relative', flexShrink: 0, width: '78px', height: '78px' }}>
                        <svg width="78" height="78" viewBox="0 0 78 78">
                          <circle cx="39" cy="39" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="5" />
                          <circle cx="39" cy="39" r={radius} fill="none" stroke={timerColor} strokeWidth="5"
                            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dashOffset}
                            transform="rotate(-90 39 39)" style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }} />
                        </svg>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '16px', fontWeight: '800', color: timerColor, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                            {String(Math.floor(ezetapCountdown / 60)).padStart(2, '0')}:{String(ezetapCountdown % 60).padStart(2, '0')}
                          </span>
                          <span style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px', fontWeight: '500' }}>remaining</span>
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>
                            Step {splitStep} — Waiting…
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontWeight: '700', color: '#374151' }}>{formatCurrency(currentAmt)}</span>
                            <span style={{ color: '#d1d5db' }}>•</span>
                            <span>{currentMode === 'CASH' ? '💵 Cash' : currentMode === 'UPI' ? '📱 UPI' : '💳 Card'}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>Complete on device</div>
                        </div>
                        <button onClick={cancelEzetapPayment}
                          style={{ flexShrink: 0, padding: '6px 14px', background: 'transparent', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Step 2 ready: Cash done, start Card/UPI */}
                {splitStep === 2 && !ezetapProcessing && !ezetapStatus && (
                  <>
                    <button onClick={() => handleSplitEzetapStep(2, splitCard, splitCardMode)}
                      style={{ width: '100%', padding: '10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                      Step 2 — Send {splitCardMode} {formatCurrency(splitCard)} to Terminal
                    </button>
                    {/* ROLLBACK(9fe12fe): slip-entry button hidden pending debug — uncomment to restore
                    renderSlipEntryOption({ kind: 'split', stepNum: 2, mode: splitCardMode, amount: parseFloat(splitCard) }) */}
                  </>
                )}

                {/* Error during split Card/UPI terminal */}
                {ezetapStatus === 'FAILED' && splitStep === 2 && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#991b1b', marginBottom: '6px' }}>❌ {ezetapError}</div>
                    <button onClick={() => { ezetapSendingRef.current = false; setEzetapStatus(''); setEzetapError(''); }}
                      style={{ padding: '6px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      Retry {splitCardMode} Payment
                    </button>
                    {/* ROLLBACK(9fe12fe): recovery panel hidden pending debug — uncomment to restore
                    {renderTxnRecovery()} */}
                  </div>
                )}

                {/* Step 3: Both complete */}
                {splitStep === 3 && (
                  <div style={{ padding: '8px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#166534', marginBottom: '4px' }}>✅ Split Payment Complete</div>
                    {splitEzetapDetails.map((d, i) => (
                      <div key={i} style={{ fontSize: '11px', color: '#374151', marginBottom: '2px' }}>
                        {i === 0 ? '💵' : splitCardMode === 'UPI' ? '📱' : '💳'} {d.paymentMode}: {formatCurrency(d.amount)}{d.txnId ? ` — Txn: ${d.txnId}` : ' — Cash confirmed'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Split UPI+Card Payment — 2-step terminal flow */}
            {paymentMethod === 'splitUpiCard' && (
              <div style={{ marginBottom: '16px', padding: '14px', background: '#eff6ff', borderRadius: '10px', border: '2px solid #3b82f6' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1d4ed8', marginBottom: '10px' }}>
                  📱💳 Split (UPI + Card) — Total: {formatCurrency(cart.total)}
                </div>

                {/* Amount inputs — only editable before step 1 starts */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                  <div>
                    <label style={{ fontSize: '13px', color: '#7c3aed', fontWeight: '700', display: 'block', marginBottom: '5px' }}>📱 UPI Amount</label>
                    <input type="number" placeholder="0.00" value={splitUpiAmount}
                      onChange={(e) => { let val = e.target.value; const num = parseFloat(val); if (num > parseFloat(cart.total)) val = parseFloat(cart.total).toFixed(2); setSplitUpiAmount(val); if (val) { setSplitCardAmount((parseFloat(cart.total) - parseFloat(val) > 0 ? (parseFloat(cart.total) - parseFloat(val)).toFixed(2) : '0.00')); } else { setSplitCardAmount(''); } }}
                      disabled={splitUpiCardStep > 0}
                      min="0" max={cart.total} step="0.01"
                      style={{ width: '100%', padding: '10px 12px', border: '2px solid #7c3aed', borderRadius: '10px', fontSize: '16px', fontWeight: '700', boxSizing: 'border-box', background: splitUpiCardStep > 0 ? '#f3f4f6' : 'white' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: '#0369a1', fontWeight: '700', display: 'block', marginBottom: '5px' }}>💳 Card Amount</label>
                    <input type="number" placeholder="0.00" value={splitCardAmount}
                      onChange={(e) => { let val = e.target.value; const num = parseFloat(val); if (num > parseFloat(cart.total)) val = parseFloat(cart.total).toFixed(2); setSplitCardAmount(val); if (val) { setSplitUpiAmount((parseFloat(cart.total) - parseFloat(val) > 0 ? (parseFloat(cart.total) - parseFloat(val)).toFixed(2) : '0.00')); } else { setSplitUpiAmount(''); } }}
                      disabled={splitUpiCardStep > 0}
                      min="0" max={cart.total} step="0.01"
                      style={{ width: '100%', padding: '10px 12px', border: '2px solid #0369a1', borderRadius: '10px', fontSize: '16px', fontWeight: '700', boxSizing: 'border-box', background: splitUpiCardStep > 0 ? '#f3f4f6' : 'white' }}
                    />
                  </div>
                </div>

                {/* Amount validation */}
                {splitUpiAmount && splitCardAmount && splitUpiCardStep === 0 && (
                  <div style={{ marginBottom: '8px', fontSize: '11px', fontWeight: '600',
                    color: Math.abs((parseFloat(splitUpiAmount) || 0) + (parseFloat(splitCardAmount) || 0) - parseFloat(cart.total)) < 0.01 ? '#27ae60' : '#c0392b'
                  }}>
                    {Math.abs((parseFloat(splitUpiAmount) || 0) + (parseFloat(splitCardAmount) || 0) - parseFloat(cart.total)) < 0.01
                      ? '✓ Amounts match' : `✗ Diff: ${formatCurrency(Math.abs((parseFloat(splitUpiAmount) || 0) + (parseFloat(splitCardAmount) || 0) - parseFloat(cart.total)))}`}
                  </div>
                )}

                {/* Step indicator */}
                {splitUpiCardStep > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', fontSize: '11px', color: '#6b7280' }}>
                    <span style={{ width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: 'white', background: splitUpiCardStep >= 2 ? '#22c55e' : '#2563eb' }}>{splitUpiCardStep >= 2 ? '✓' : '1'}</span>
                    <span style={{ color: splitUpiCardStep >= 2 ? '#22c55e' : '#2563eb', fontWeight: '600' }}>UPI {formatCurrency(splitUpiAmount)}</span>
                    <span style={{ flex: 1, height: '1px', background: splitUpiCardStep >= 2 ? '#22c55e' : '#d1d5db' }} />
                    <span style={{ width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: 'white', background: splitUpiCardStep === 3 ? '#22c55e' : splitUpiCardStep === 2 ? '#2563eb' : '#d1d5db' }}>{splitUpiCardStep === 3 ? '✓' : '2'}</span>
                    <span style={{ color: splitUpiCardStep === 3 ? '#22c55e' : splitUpiCardStep === 2 ? '#2563eb' : '#9ca3af', fontWeight: '600' }}>Card {formatCurrency(splitCardAmount)}</span>
                  </div>
                )}

                {/* Pincode verification needed message */}
                {splitUpiCardStep === 0 && splitUpiAmount && splitCardAmount && Math.abs((parseFloat(splitUpiAmount) || 0) + (parseFloat(splitCardAmount) || 0) - parseFloat(cart.total)) < 0.01 && hasWarehouseItems && !pincodeVerified && (
                  <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', marginTop: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                      📱💳 Verify delivery pincode above to enable UPI + Card payment
                    </div>
                  </div>
                )}

                {/* Step 0: Start UPI payment on terminal */}
                {splitUpiCardStep === 0 && splitUpiAmount && splitCardAmount && Math.abs((parseFloat(splitUpiAmount) || 0) + (parseFloat(splitCardAmount) || 0) - parseFloat(cart.total)) < 0.01 && (!hasWarehouseItems || pincodeVerified) && (
                  <>
                  <button onClick={() => {
                    if (!selectedCustomer) { alert('Please select a customer before proceeding'); return; }
                    if (!validatePrePayment()) return;
                    handleSplitUpiCardStep(1, splitUpiAmount, 'UPI');
                  }}
                    disabled={!selectedCustomer}
                    style={{ width: '100%', padding: '10px', background: !selectedCustomer ? '#9ca3af' : '#7c3aed', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: !selectedCustomer ? 'not-allowed' : 'pointer' }}>
                    Step 1 — Send UPI {formatCurrency(splitUpiAmount)} to Terminal
                  </button>
                  {!selectedCustomer && (
                    <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '6px', textAlign: 'center' }}>⚠️ Select a customer above to proceed</div>
                  )}
                  {/* ROLLBACK(9fe12fe): slip-entry button hidden pending debug — uncomment to restore
                  renderSlipEntryOption({ kind: 'splitUpiCard', stepNum: 1, mode: 'UPI', amount: parseFloat(splitUpiAmount) }) */}
                  </>
                )}

                {/* Terminal waiting UI for current step */}
                {(splitUpiCardStep === 1 || (splitUpiCardStep === 2 && ezetapProcessing)) && (ezetapStatus === 'INITIATED' || ezetapStatus === 'POLLING') && (() => {
                  const currentMode = splitUpiCardStep === 1 ? 'UPI' : 'CARD';
                  const currentAmt = splitUpiCardStep === 1 ? splitUpiAmount : splitCardAmount;
                  const isUpi = currentMode === 'UPI';
                  const totalSec = isUpi ? EZETAP_TIMEOUT_SECONDS.UPI : EZETAP_TIMEOUT_SECONDS.CARD;
                  const progress = totalSec > 0 ? ezetapCountdown / totalSec : 0;
                  const radius = 34;
                  const circ = 2 * Math.PI * radius;
                  const dashOffset = circ * (1 - progress);
                  const isUrgent = ezetapCountdown <= 30;
                  const timerColor = isUrgent ? '#ef4444' : '#2563eb';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '4px 0' }}>
                      <div style={{ position: 'relative', flexShrink: 0, width: '78px', height: '78px' }}>
                        <svg width="78" height="78" viewBox="0 0 78 78">
                          <circle cx="39" cy="39" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="5" />
                          <circle cx="39" cy="39" r={radius} fill="none" stroke={timerColor} strokeWidth="5"
                            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dashOffset}
                            transform="rotate(-90 39 39)" style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }} />
                        </svg>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '16px', fontWeight: '800', color: timerColor, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                            {String(Math.floor(ezetapCountdown / 60)).padStart(2, '0')}:{String(ezetapCountdown % 60).padStart(2, '0')}
                          </span>
                          <span style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px', fontWeight: '500' }}>remaining</span>
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>
                            Step {splitUpiCardStep} — Waiting…
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontWeight: '700', color: '#374151' }}>{formatCurrency(currentAmt)}</span>
                            <span style={{ color: '#d1d5db' }}>•</span>
                            <span>{currentMode === 'UPI' ? '📱 UPI' : '💳 Card'}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>Complete on device</div>
                        </div>
                        <button onClick={cancelEzetapPayment}
                          style={{ flexShrink: 0, padding: '6px 14px', background: 'transparent', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Step 2 ready: UPI done, start Card */}
                {splitUpiCardStep === 2 && !ezetapProcessing && !ezetapStatus && (
                  <>
                    <button onClick={() => handleSplitUpiCardStep(2, splitCardAmount, 'CARD')}
                      style={{ width: '100%', padding: '10px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                      Step 2 — Send Card {formatCurrency(splitCardAmount)} to Terminal
                    </button>
                    {/* ROLLBACK(9fe12fe): slip-entry button hidden pending debug — uncomment to restore
                    renderSlipEntryOption({ kind: 'splitUpiCard', stepNum: 2, mode: 'CARD', amount: parseFloat(splitCardAmount) }) */}
                  </>
                )}

                {/* Error during split terminal */}
                {ezetapStatus === 'FAILED' && (splitUpiCardStep === 1 || splitUpiCardStep === 2) && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#991b1b', marginBottom: '6px' }}>❌ {ezetapError}</div>
                    <button onClick={() => { ezetapSendingRef.current = false; setEzetapStatus(''); setEzetapError(''); }}
                      style={{ padding: '6px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      Retry {splitUpiCardStep === 1 ? 'UPI' : 'Card'} Payment
                    </button>
                    {/* ROLLBACK(9fe12fe): recovery panel hidden pending debug — uncomment to restore
                    {renderTxnRecovery()} */}
                  </div>
                )}

                {/* Step 3: Both complete */}
                {splitUpiCardStep === 3 && (
                  <div style={{ padding: '8px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#166534', marginBottom: '4px' }}>✅ Split Payment Complete (UPI + Card)</div>
                    {splitUpiCardDetails.map((d, i) => (
                      <div key={i} style={{ fontSize: '11px', color: '#374151', marginBottom: '2px' }}>
                        {i === 0 ? '📱' : '💳'} {d.paymentMode}: {formatCurrency(d.amount)}{d.txnId ? ` — Txn: ${d.txnId}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Ezetap Payment Terminal — Card / UPI only (cash is physical, no terminal needed) */}
            {(paymentMethod === 'card' || paymentMethod === 'upi') && (fulfillmentSource !== 'warehouse' || pincodeVerified) && (
              <div style={{ marginBottom: '16px', padding: '16px', background: ezetapStatus === 'SUCCESS' ? '#f0fdf4' : ezetapStatus === 'FAILED' ? '#fef2f2' : '#f8fafc', borderRadius: '10px', border: `1.5px solid ${ezetapStatus === 'SUCCESS' ? '#86efac' : ezetapStatus === 'FAILED' ? '#fca5a5' : '#e2e8f0'}` }}>
                {!ezetapStatus && !ezetapProcessing && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
                      {paymentMethod === 'cash' ? '💵' : paymentMethod === 'upi' ? '📱' : '💳'} {paymentMethod === 'cash' ? 'Cash' : paymentMethod === 'upi' ? 'UPI' : 'Card'} Payment — Terminal
                    </div>
                    <button onClick={handleEzetapPayment}
                      disabled={!selectedCustomer}
                      className="checkout-btn-primary"
                      style={{ width: '100%', padding: '12px', background: !selectedCustomer ? '#9ca3af' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: !selectedCustomer ? 'not-allowed' : 'pointer', transition: 'all 0.15s', boxShadow: !selectedCustomer ? 'none' : '0 4px 12px rgba(37,99,235,0.3)' }}>
                      Send to Terminal — {formatCurrency(cart.total)}
                    </button>
                    {!selectedCustomer && (
                      <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '6px', textAlign: 'center' }}>⚠️ Select a customer above to enable terminal payment</div>
                    )}
                    {/* ROLLBACK(9fe12fe): slip-entry button hidden pending debug — uncomment to restore
                    renderSlipEntryOption({ kind: 'single', stepNum: 0, mode: paymentMethod === 'upi' ? 'UPI' : 'CARD', amount: parseFloat(cart.total) }) */}
                  </div>
                )}

                {(ezetapStatus === 'INITIATED' || ezetapStatus === 'POLLING') && (() => {
                  const isUpi = paymentMethod === 'upi';
                  const totalSec = isUpi ? EZETAP_TIMEOUT_SECONDS.UPI : EZETAP_TIMEOUT_SECONDS.CARD;
                  const progress = totalSec > 0 ? ezetapCountdown / totalSec : 0;
                  const radius = 34;
                  const circ = 2 * Math.PI * radius;
                  const dashOffset = circ * (1 - progress);
                  const isUrgent = ezetapCountdown <= 30;
                  const timerColor = isUrgent ? '#ef4444' : '#2563eb';
                  return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '4px 0' }}>
                    {/* Circular countdown timer */}
                    <div style={{ position: 'relative', flexShrink: 0, width: '78px', height: '78px' }}>
                      <svg width="78" height="78" viewBox="0 0 78 78">
                        <circle cx="39" cy="39" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="5" />
                        <circle cx="39" cy="39" r={radius} fill="none" stroke={timerColor} strokeWidth="5"
                          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dashOffset}
                          transform="rotate(-90 39 39)" style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }} />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: timerColor, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                          {String(Math.floor(ezetapCountdown / 60)).padStart(2, '0')}:{String(ezetapCountdown % 60).padStart(2, '0')}
                        </span>
                        <span style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px', fontWeight: '500' }}>remaining</span>
                      </div>
                    </div>

                    {/* Info + cancel */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>
                          Waiting for payment…
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontWeight: '700', color: '#374151' }}>{formatCurrency(cart.total)}</span>
                          <span style={{ color: '#d1d5db' }}>•</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                            {paymentMethod === 'cash' ? '💵' : paymentMethod === 'upi' ? '📱' : '💳'}
                            {paymentMethod === 'cash' ? 'Cash' : paymentMethod === 'upi' ? 'UPI' : 'Card'}
                          </span>
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          Please complete on device
                        </div>
                        {ezetapRefNumber && (
                          <div style={{ fontSize: '10px', color: '#a1a1aa', marginTop: '2px', fontFamily: 'monospace' }}>
                            Ref: {ezetapRefNumber}
                          </div>
                        )}
                      </div>
                      <button onClick={cancelEzetapPayment}
                        style={{ flexShrink: 0, padding: '6px 14px', background: 'transparent', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.15s', whiteSpace: 'nowrap' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        Cancel
                      </button>
                    </div>
                  </div>
                  );
                })()}

                {ezetapStatus === 'SUCCESS' && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#166534', marginBottom: '4px' }}>
                      ✅ Payment Successful
                    </div>
                    <div style={{ fontSize: '12px', color: '#166534', marginBottom: '2px' }}>
                      Txn: {transactionId}
                    </div>
                    {ezetapDetails?.paymentMode && (
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>
                        Mode: {ezetapDetails.paymentMode}
                      </div>
                    )}
                    {ezetapDetails?.receiptUrl && (
                      <a href={ezetapDetails.receiptUrl} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '11px', color: '#2563eb', textDecoration: 'underline' }}>
                        View Receipt
                      </a>
                    )}
                  </div>
                )}

                {ezetapStatus === 'FAILED' && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#991b1b', marginBottom: '4px' }}>
                      ❌ {ezetapError || 'Payment Failed'}
                    </div>
                    <button onClick={() => { ezetapSendingRef.current = false; setEzetapStatus(''); setEzetapError(''); }}
                      style={{ marginTop: '6px', padding: '6px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      Retry
                    </button>
                    {/* ROLLBACK(9fe12fe): recovery panel hidden pending debug — uncomment to restore
                    {renderTxnRecovery()} */}
                  </div>
                )}
              </div>
            )}

            {/* District by Zomato / NetBanking — Transaction ID input */}
            {(paymentMethod === 'district' || paymentMethod === 'netbanking') && (
              <div style={{ marginBottom: '16px', padding: '16px', background: paymentMethod === 'district' ? 'linear-gradient(135deg, #fef2f2, #fee2e2)' : 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', borderRadius: '14px', border: `1.5px solid ${paymentMethod === 'district' ? '#fca5a5' : '#93c5fd'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: paymentMethod === 'district' ? '#991b1b' : '#1e40af', marginBottom: '8px' }}>
                  {paymentMethod === 'district' ? '🔴 District by Zomato' : '🏦 NetBanking / NEFT / RTGS / Bank Transfer'} — Enter Transaction ID
                </div>
                <input
                  type="text"
                  placeholder={`${paymentMethod === 'district' ? 'District' : 'NEFT/RTGS/Transfer'} Transaction ID`}
                  value={transactionId}
                  // Cap at 128 chars — the orders.transaction_id column is VARCHAR(128); a longer
                  // value fails the DB insert and the order silently doesn't get saved.
                  maxLength={128}
                  onChange={(e) => setTransactionId(e.target.value.slice(0, 128))}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${transactionId.length >= 128 ? '#dc2626' : (paymentMethod === 'district' ? '#fca5a5' : '#93c5fd')}`, borderRadius: '10px', fontSize: '13px', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                  onFocus={(e) => e.target.style.borderColor = transactionId.length >= 128 ? '#dc2626' : (paymentMethod === 'district' ? '#ef4444' : '#3b82f6')}
                  onBlur={(e) => e.target.style.borderColor = transactionId.length >= 128 ? '#dc2626' : (paymentMethod === 'district' ? '#fca5a5' : '#93c5fd')}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', gap: '8px' }}>
                  {transactionId.trim()
                    ? <span style={{ fontSize: '11px', fontWeight: '600', color: '#16a34a' }}>✓ Transaction ID entered</span>
                    : <span style={{ fontSize: '11px', color: '#dc2626' }}>⚠️ Transaction ID is mandatory</span>}
                  <span style={{ fontSize: '11px', fontWeight: '600', color: transactionId.length >= 128 ? '#dc2626' : '#9ca3af', whiteSpace: 'nowrap' }}>{transactionId.length}/128</span>
                </div>
                {transactionId.length >= 128 && (
                  <div style={{ marginTop: '4px', fontSize: '11px', fontWeight: '600', color: '#dc2626' }}>⚠️ Transaction ID cannot exceed 128 characters.</div>
                )}
              </div>
            )}

            {/* Snapmint EMI — QR Code + Link + Polling */}
            {paymentMethod === 'snapmint' && (
              <div style={{ marginBottom: '16px', padding: '16px', background: 'linear-gradient(135deg, #fefce8, #fef9c3)', borderRadius: '14px', border: '1.5px solid #fbbf24', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                {/* Not started — show initiate button */}
                {!snapmintStatus && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#92400e', marginBottom: '10px' }}>⚡ Snapmint EMI Payment</div>
                    <button
                      onClick={handleSnapmintPayment}
                      disabled={!selectedCustomer}
                      style={{ padding: '10px 28px', background: selectedCustomer ? '#f59e0b' : '#d1d5db', color: selectedCustomer ? '#fff' : '#9ca3af', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: selectedCustomer ? 'pointer' : 'not-allowed' }}>
                      Initiate Snapmint EMI
                    </button>
                    {!selectedCustomer && (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: '#dc2626' }}>⚠️ Select a customer first</div>
                    )}
                  </div>
                )}

                {/* Initiated — waiting for response */}
                {snapmintStatus === 'INITIATED' && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400e' }}>Initiating Snapmint checkout...</div>
                  </div>
                )}

                {/* Polling — show QR left + circular timer right */}
                {snapmintStatus === 'POLLING' && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', marginBottom: '10px', textAlign: 'center' }}>⚡ Scan QR or open link to complete EMI payment</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      {/* QR Code — left */}
                      {snapmintQr && (
                        <div style={{ flexShrink: 0 }}>
                          <img src={snapmintQr} alt="Snapmint QR Code" style={{ width: '120px', height: '120px', borderRadius: '8px', border: '2px solid #fbbf24' }} />
                        </div>
                      )}
                      {/* Circular timer + controls — right */}
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {(() => {
                          const totalSec = 600;
                          const progress = totalSec > 0 ? snapmintCountdown / totalSec : 0;
                          const radius = 34;
                          const circ = 2 * Math.PI * radius;
                          const dashOffset = circ * (1 - progress);
                          const isUrgent = snapmintCountdown <= 60;
                          const timerColor = isUrgent ? '#ef4444' : '#f59e0b';
                          return (
                            <div style={{ position: 'relative', flexShrink: 0, width: '78px', height: '78px' }}>
                              <svg width="78" height="78" viewBox="0 0 78 78">
                                <circle cx="39" cy="39" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="5" />
                                <circle cx="39" cy="39" r={radius} fill="none" stroke={timerColor} strokeWidth="5"
                                  strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dashOffset}
                                  transform="rotate(-90 39 39)" style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }} />
                              </svg>
                              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: '16px', fontWeight: '800', color: timerColor, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                                  {String(Math.floor(snapmintCountdown / 60)).padStart(2, '0')}:{String(snapmintCountdown % 60).padStart(2, '0')}
                                </span>
                                <span style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px', fontWeight: '500' }}>remaining</span>
                              </div>
                            </div>
                          );
                        })()}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', minWidth: '110px' }}>
                          {snapmintUrl && (
                            <a href={snapmintUrl} target="_blank" rel="noopener noreferrer"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', width: '100%', padding: '8px 18px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', borderRadius: '8px', fontSize: '12px', fontWeight: '700', textDecoration: 'none', textAlign: 'center', boxShadow: '0 2px 8px rgba(245,158,11,0.35)', transition: 'transform 0.15s, box-shadow 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(245,158,11,0.45)'; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(245,158,11,0.35)'; }}>
                              Open Link ↗
                            </a>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 10px', background: '#f0fdf4', borderRadius: '16px', border: '1px solid #bbf7d0' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'pulse 1.5s ease-in-out infinite', boxShadow: '0 0 4px #22c55e' }} />
                            <span style={{ fontSize: '10px', fontWeight: '600', color: '#15803d' }}>Checking status...</span>
                          </div>
                          <button onClick={cancelSnapmintPayment}
                            style={{ width: '100%', padding: '6px 14px', background: 'transparent', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: '8px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s', letterSpacing: '0.3px' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#fecaca'; }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Success */}
                {snapmintStatus === 'SUCCESS' && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', marginBottom: '6px' }}>✅</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#166534' }}>Snapmint Payment Successful!</div>
                    <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>Transaction ID: {transactionId}</div>
                  </div>
                )}

                {/* Failed */}
                {snapmintStatus === 'FAILED' && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#991b1b', marginBottom: '4px' }}>
                      ❌ {snapmintError || 'Payment Failed'}
                    </div>
                    <button onClick={() => { setSnapmintStatus(''); setSnapmintError(''); setSnapmintOrderId(''); setSnapmintDetails(null); }}
                      style={{ marginTop: '6px', padding: '6px 16px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      Retry
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Ezetap waiting for pincode — Card/UPI/Cash warehouse without pincode */}
            {(paymentMethod === 'card' || paymentMethod === 'upi' || paymentMethod === 'cash') && fulfillmentSource === 'warehouse' && !pincodeVerified && !gstBilling && (
              <div style={{ marginBottom: '12px', padding: '10px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                  {paymentMethod === 'cash' ? '💵' : '💳'} Verify delivery pincode above to enable {paymentMethod === 'cash' ? 'Cash' : paymentMethod === 'upi' ? 'UPI' : 'Card'} payment
                </div>
              </div>
            )}

            {/* Cash Calculator */}
            {paymentMethod === 'cash' && (() => {
              const invoiceAmt = parseFloat(cart.total);
              const received = parseFloat(cashReceived) || 0;
              const change = received - invoiceAmt;
              return (
                <div style={{ marginBottom: '12px', padding: '10px 12px', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderRadius: '16px', border: '1.5px solid #86efac', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#166534', whiteSpace: 'nowrap' }}>💵 {formatCurrency(invoiceAmt)}</span>
                  <input type="number" placeholder="Received" value={cashReceived} onChange={(e) => setCashReceived(e.target.value)} min="0" step="1"
                    style={{ width: '100px', padding: '5px 6px', border: '1px solid #bbf7d0', borderRadius: '10px', fontSize: '13px', fontWeight: '600', outline: 'none' }}
                    onFocus={(e) => e.target.style.borderColor = '#22c55e'} onBlur={(e) => e.target.style.borderColor = '#bbf7d0'}
                  />
                  {cashReceived && (
                    <span style={{ marginLeft: 'auto', fontSize: '13px', fontWeight: '700', color: change >= 0 ? '#166534' : '#991b1b', whiteSpace: 'nowrap' }}>
                      {change >= 0 ? `Return: ${formatCurrency(change)}` : `Short: ${formatCurrency(Math.abs(change))}`}
                    </span>
                  )}
                </div>
              );
            })()}

          </div>
        </div>

        {/* Footer — Checkout Button */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', flexShrink: 0, background: '#ffffff' }}>
          {!paymentMethod && (
            <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600', textAlign: 'center', marginBottom: '8px', background: '#fef2f2', padding: '6px 12px', borderRadius: '8px', border: '1px solid #fecaca' }}>⚠️ Please select a payment method</div>
          )}
          <button onClick={handleCheckout} disabled={processing || ezetapProcessing || snapmintProcessing || !paymentMethod}
            className="checkout-btn-primary"
            style={{ width: '100%', padding: '14px', background: (processing || ezetapProcessing || snapmintProcessing) ? '#cbd5e1' : !paymentMethod ? '#cbd5e1' : '#1e293b', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: (processing || ezetapProcessing || snapmintProcessing || !paymentMethod) ? 'not-allowed' : 'pointer', letterSpacing: '-0.2px', transition: 'all 0.2s', boxShadow: (processing || ezetapProcessing || snapmintProcessing || !paymentMethod) ? 'none' : '0 4px 12px rgba(30,41,59,0.25)' }}>
            {processing ? 'Processing...' : ezetapProcessing ? 'Waiting for terminal...' : snapmintProcessing ? 'Waiting for Snapmint...' : `Complete Purchase — ${formatCurrency(cart.total)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CheckoutModal;
